import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend, LineChart, Line,
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, PieChart as PieChartIcon,
  Wallet, Percent, Activity, Info, ChevronDown, ChevronUp,
  BarChart2, Layers, Shield, Zap, AlertCircle, Calculator,
  RefreshCw, Clock, Star, Save, Pencil, Trash2, Brain,
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import InvestmentAIAdvisor from './InvestmentAIAdvisor';
import {
  format, subMonths, startOfMonth, endOfMonth,
  eachMonthOfInterval, addMonths, eachDayOfInterval, startOfDay,
  eachWeekOfInterval, endOfWeek, startOfWeek,
} from 'date-fns';
import { it } from 'date-fns/locale';

// ─────────────────────────────────────────────────────────────────────────────
// Classification helpers
// ─────────────────────────────────────────────────────────────────────────────
const classifyWallet = (wallet) => {
  const t = (wallet.type || '').toLowerCase();
  if (t.includes('deposito')) return 'deposito';
  if (t.includes('etf')) return 'etf';
  if (t.includes('azione') || t.includes('azioni')) return 'azioni';
  if (t.includes('obbligazione') || t.includes('obbligazioni')) return 'obbligazioni';
  return null;
};

const isSecurityWallet = (w) => {
  const c = classifyWallet(w);
  return c === 'etf' || c === 'azioni' || c === 'obbligazioni';
};
const isDepositWallet = (w) => classifyWallet(w) === 'deposito';
const isInvestmentWallet = (w) => classifyWallet(w) !== null;
const isLiquidWallet = (w) => !isInvestmentWallet(w);

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_COLOR = { etf: '#eab308', azioni: '#6366f1', obbligazioni: '#f97316', deposito: '#10b981', liquido: '#0ea5e9' };
const COMPARISON_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#0ea5e9', '#f97316', '#ec4899'];
const TYPE_LABEL = { etf: 'ETF', azioni: 'Azioni', obbligazioni: 'Obbligazioni', deposito: 'Conto Deposito', liquido: 'Liquidità' };
const TYPE_ICON  = { etf: '🧺', azioni: '📈', obbligazioni: '📜', deposito: '🏦', liquido: '💧' };

// ─────────────────────────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '€0.00';
  try { return formatCurrency(num); } catch { return `€${num.toFixed(2)}`; }
};
const fmtShort = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '€0';
  if (Math.abs(n) >= 1_000_000) return `€${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `€${(n / 1_000).toFixed(0)}k`;
  return `€${n.toFixed(0)}`;
};
const pct = (num, total) => (total !== 0 ? ((num / total) * 100).toFixed(1) : '0.0');

// ─────────────────────────────────────────────────────────────────────────────
// Yahoo Finance: fetch 20-year monthly close prices → compute CAGR
// ─────────────────────────────────────────────────────────────────────────────
const YAHOO_CACHE = {};   // isinCode → { cagr, years, firstDate, lastDate, closes }

async function fetchYahooCagr(ticker) {
  if (!ticker || ticker.trim() === '') return null;
  const key = ticker.toUpperCase().trim();
  if (YAHOO_CACHE[key]) return YAHOO_CACHE[key];

  try {
    const url = `/api/yahoo/v8/finance/chart/${encodeURIComponent(key)}?range=20y&interval=1mo`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();

    const result = json?.chart?.result?.[0];
    if (!result) return null;

    const closes = result.indicators?.adjclose?.[0]?.adjclose ?? result.indicators?.quote?.[0]?.close;
    const timestamps = result.timestamp;
    if (!closes || !timestamps || closes.length < 2) return null;

    // Filter nulls
    const pairs = timestamps
      .map((ts, i) => ({ ts: ts * 1000, close: closes[i] }))
      .filter(p => p.close != null && Number.isFinite(p.close));

    if (pairs.length < 2) return null;

    const first = pairs[0];
    const last  = pairs[pairs.length - 1];
    const yearsElapsed = (last.ts - first.ts) / (365.25 * 24 * 3600 * 1000);
    if (yearsElapsed < 0.5) return null;

    const cagrFraction = Math.pow(last.close / first.close, 1 / yearsElapsed) - 1;
    const cagrPct = cagrFraction * 100;

    // Monthly closes for sparkline (last 60 months)
    const sparkline = pairs.slice(-60).map(p => ({
      ts: p.ts,
      label: format(new Date(p.ts), "MMM ''yy", { locale: it }),
      close: p.close,
    }));

    const data = {
      cagr: parseFloat(cagrPct.toFixed(2)),
      years: parseFloat(yearsElapsed.toFixed(1)),
      firstDate: new Date(first.ts),
      lastDate: new Date(last.ts),
      firstClose: first.close,
      lastClose: last.close,
      currency: result.meta?.currency ?? '',
      longName: result.meta?.longName ?? '',
      sparkline,
    };

    YAHOO_CACHE[key] = data;
    return data;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Yahoo Finance: price history with localStorage 24h cache + in-memory cache
// ─────────────────────────────────────────────────────────────────────────────
const YAHOO_PRICES_CACHE = {};

async function fetchYahooPrices(ticker, period) {
  if (!ticker || !ticker.trim()) return null;
  const key = `${ticker.toUpperCase().trim()}_${period}`;
  if (YAHOO_PRICES_CACHE[key]) return YAHOO_PRICES_CACHE[key];
  const lsKey = `yahoo_p_${key}`;
  try {
    const raw = localStorage.getItem(lsKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts < 86400000) {
        const result = { data: parsed.data, currency: parsed.currency };
        YAHOO_PRICES_CACHE[key] = result;
        return result;
      }
    }
  } catch {}
  try {
    const rangeMap  = { '6M': '6mo', '12M': '1y', 'MAX': 'max' };
    const intrvMap  = { '6M': '1d',  '12M': '1d', 'MAX': '1mo' };
    const url = `/api/yahoo/v8/finance/chart/${encodeURIComponent(ticker.toUpperCase().trim())}?range=${rangeMap[period]}&interval=${intrvMap[period]}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const r = json?.chart?.result?.[0];
    if (!r) return null;
    const closes = r.indicators?.adjclose?.[0]?.adjclose ?? r.indicators?.quote?.[0]?.close;
    const timestamps = r.timestamp;
    if (!closes || !timestamps) return null;
    const currency = r.meta?.currency ?? '';
    const pairs = timestamps
      .map((ts, i) => ({ ts: ts * 1000, close: closes[i] }))
      .filter(p => p.close != null && Number.isFinite(p.close));
    const data = pairs.map(p => ({
      ts: p.ts,
      label: period === 'MAX'
        ? format(new Date(p.ts), "MMM ''yy", { locale: it })
        : format(new Date(p.ts), 'dd/MM', { locale: it }),
      close: +p.close.toFixed(3),
    }));
    const result = { data, currency };
    YAHOO_PRICES_CACHE[key] = result;
    try { localStorage.setItem(lsKey, JSON.stringify({ ts: Date.now(), data, currency })); } catch {}
    return result;
  } catch { return null; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────────────────────
const InvTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl px-3 py-2 text-xs min-w-[130px]">
      {label && <p className="font-bold text-gray-500 dark:text-gray-400 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color || p.fill }} />
          <span className="text-gray-600 dark:text-gray-400 truncate">{p.name}:</span>
          <span className="font-bold text-gray-900 dark:text-gray-100 ml-auto">
            {typeof p.value === 'number' ? fmtShort(p.value) : (p.value ?? '—')}
          </span>
        </div>
      ))}
    </div>
  );
};

const StackedTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-4 text-xs space-y-2 min-w-[180px]">
      <p className="font-black text-gray-500 dark:text-gray-400 uppercase text-[10px] tracking-wider">{data.tooltipLabel || label}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#0ea5e9' }} />
            <span className="text-gray-600 dark:text-gray-400 font-semibold">Liquidità:</span>
          </div>
          <span className="font-bold text-gray-900 dark:text-gray-100">{fmt(data.liquidita)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#10b981' }} />
            <span className="text-gray-600 dark:text-gray-400 font-semibold">Deposito:</span>
          </div>
          <span className="font-bold text-gray-900 dark:text-gray-100">{fmt(data.deposito)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#8b5cf6' }} />
            <span className="text-gray-600 dark:text-gray-400 font-semibold">Titoli:</span>
          </div>
          <span className="font-bold text-gray-900 dark:text-gray-100">{fmt(data.titoli)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#eab308' }} />
            <span className="text-gray-600 dark:text-gray-400 font-semibold">Rendimento:</span>
          </div>
          <span className="font-bold text-emerald-600 dark:text-emerald-400">{fmt(data.interessi)}</span>
        </div>
      </div>
      <div className="border-t border-gray-100 dark:border-gray-700/60 pt-2 flex items-center justify-between gap-4 font-black">
        <span className="text-gray-900 dark:text-gray-100 text-sm">Totale:</span>
        <span className="text-blue-600 dark:text-blue-400 text-sm">{fmt(data.total)}</span>
      </div>
    </div>
  );
};

const SavingsInvestmentTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-3.5 text-xs space-y-2 min-w-[170px]">
      <p className="font-black text-gray-500 dark:text-gray-400 uppercase text-[10px] tracking-wider">{label}</p>
      <div className="space-y-1.5">
        {payload.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color || p.fill }} />
              <span className="text-gray-600 dark:text-gray-400 font-semibold">{p.name}:</span>
            </div>
            <span className="font-bold text-gray-900 dark:text-gray-100">
              {typeof p.value === 'number' ? fmt(p.value) : p.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, sub, icon: Icon, color, badge }) => (
  <div className="card p-3.5 flex gap-3 items-start relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.04]" style={{ background: `radial-gradient(circle at top right, ${color}, transparent)` }} />
    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `${color}18`, color }}>
      {Icon && <Icon className="w-4.5 h-4.5" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 dark:text-gray-400 mb-0.5 truncate">{label}</div>
      <div className="text-lg font-black text-gray-900 dark:text-gray-100 leading-none truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">{sub}</div>}
      {badge && (
        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${color}18`, color }}>
          {badge}
        </span>
      )}
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, title, sub, color = '#6366f1' }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18`, color }}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-none">{title}</h3>
      {sub && <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SecurityDetail — expanded detail for a grouped security
// ─────────────────────────────────────────────────────────────────────────────
const SecurityDetail = ({ security, incomes, expenses, portfolioValue, updateDocument, grossAnnualRate }) => {
  const [chartPeriod, setChartPeriod] = useState('12M');
  const [projYears, setSecProjYears] = useState(0);
  const [projMode, setProjMode] = useState('lump');
  const [projAmount, setProjAmount] = useState(0);
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState(String(security.expectedRate || ''));
  const [priceData, setPriceData] = useState(null);
  const [pricePeriod, setPricePeriod] = useState('12M');
  const [priceLoading, setPriceLoading] = useState(false);

  const color = security.color || '#6366f1';
  const balance = security.balance;
  const safeId = useMemo(() => security.key.replace(/[^a-z0-9]/g, ''), [security.key]);
  const accountIds = useMemo(() => new Set(security.accounts.map(w => w.id)), [security.accounts]);

  // For multi-account tiles (Generale), exclude Trasferimento incomes that are internal
  // (i.e. there is a matching Trasferimento expense from another wallet in the same group on the same date+amount)
  const internalTransferIds = useMemo(() => {
    const result = new Set();
    if (accountIds.size <= 1) return result;
    const expenseKeys = new Set();
    expenses.forEach(tx => {
      if (!accountIds.has(tx.walletId) || tx.category !== 'Trasferimento') return;
      const day = tx.date ? new Date(tx.date).toISOString().slice(0, 10) : '';
      expenseKeys.add(`${day}|${Number(tx.amount) || 0}`);
    });
    incomes.forEach(tx => {
      if (!accountIds.has(tx.walletId) || tx.category !== 'Trasferimento') return;
      const day = tx.date ? new Date(tx.date).toISOString().slice(0, 10) : '';
      if (expenseKeys.has(`${day}|${Number(tx.amount) || 0}`)) result.add(tx.id);
    });
    return result;
  }, [incomes, expenses, accountIds]);

  // Capital & Interest from income transactions
  const { capital, interest, firstDate } = useMemo(() => {
    let capitalSum = 0, interestSum = 0, firstDate = null;
    incomes.forEach(tx => {
      if (!accountIds.has(tx.walletId)) return;
      if (internalTransferIds.has(tx.id)) return;
      const amt = Number(tx.amount) || 0;
      if (tx.category === 'Investimenti') interestSum += amt;
      else capitalSum += amt;
      if (tx.date) {
        const d = new Date(tx.date);
        if (!firstDate || d < firstDate) firstDate = d;
      }
    });
    return { capital: capitalSum, interest: interestSum, firstDate };
  }, [incomes, accountIds, internalTransferIds]);

  // Always cut at end of previous month (last complete month)
  const chartNow = useMemo(() => endOfMonth(subMonths(new Date(), 1)), []);

  // Stacked area chart: cumulative capital + interest by month, up to end of last month
  const stackedChartData = useMemo(() => {
    const startDate = chartPeriod === '6M' ? subMonths(chartNow, 6)
      : chartPeriod === '12M' ? subMonths(chartNow, 12)
      : (firstDate || subMonths(chartNow, 24));
    const months = eachMonthOfInterval({ start: startOfMonth(startDate), end: startOfMonth(chartNow) });
    return months.map(month => {
      const end = endOfMonth(month);
      const label = format(month, "MMM ''yy", { locale: it });
      let cumCap = 0, cumInt = 0;
      incomes.forEach(tx => {
        if (!accountIds.has(tx.walletId) || !tx.date) return;
        if (internalTransferIds.has(tx.id)) return;
        if (new Date(tx.date) > end) return;
        const amt = Number(tx.amount) || 0;
        if (tx.category === 'Investimenti') cumInt += amt;
        else cumCap += amt;
      });
      return { label, tooltipLabel: label, capital: Math.max(0, cumCap), interest: Math.max(0, cumInt) };
    });
  }, [incomes, accountIds, internalTransferIds, chartPeriod, firstDate, chartNow]);

  // Extend historical data with projected stacked capital + interest (same visual style)
  const combinedChartData = useMemo(() => {
    const r = parseFloat(security.expectedRate) || 0;
    if (projYears === 0 || r === 0) return stackedChartData;
    const rM = r / 100 / 12;
    const lastPoint = stackedChartData[stackedChartData.length - 1];
    const lastHistCap = lastPoint?.capital || 0;
    const lastHistInt = lastPoint?.interest || 0;
    // Project from the chart's last point (capital + interest) so the curve is always
    // visually continuous, regardless of withdrawals that may have reduced the wallet balance
    // below the cumulative capital sum.
    const lastHistTotal = lastHistCap + lastHistInt;
    const totalMonths = projYears * 12;

    // Sparsify historical labels so only the last point shows its label when projection is active
    const histPoints = stackedChartData.map((p, i) => ({
      ...p,
      tooltipLabel: p.tooltipLabel || p.label,
      label: i === stackedChartData.length - 1 ? (p.label || 'ora') : '',
    }));

    // Monthly projection points — same granularity as historical data for time-consistent spacing
    const futurePoints = Array.from({ length: totalMonths }, (_, i) => {
      const m = i + 1;
      const yr = m / 12;
      let totalVal, projCap;
      if (projMode === 'lump') {
        totalVal = (lastHistTotal + projAmount) * Math.pow(1 + r / 100, yr);
        projCap = lastHistCap + projAmount;
      } else {
        totalVal = lastHistTotal * Math.pow(1 + r / 100, yr);
        totalVal += rM > 0
          ? projAmount * (Math.pow(1 + rM, m) - 1) / rM
          : projAmount * m;
        projCap = lastHistCap + projAmount * m;
      }
      const projInt = Math.max(0, Math.round(totalVal) - Math.round(projCap));
      return {
        label: m % 12 === 0 ? `+${m / 12}a` : '',
        tooltipLabel: format(addMonths(chartNow, m), "MMM yyyy", { locale: it }),
        capital: Math.round(projCap),
        interest: projInt,
        isProjected: true,
      };
    });
    return [...histPoints, ...futurePoints];
  }, [stackedChartData, projYears, projMode, projAmount, security.expectedRate, balance]);

  // Effective annual return (CAGR from first deposit to now)
  const effectiveReturn = useMemo(() => {
    if (capital <= 0 || !firstDate || balance <= 0) return null;
    const years = (Date.now() - firstDate.getTime()) / (365.25 * 24 * 3600 * 1000);
    if (years < 0.1) return null;
    const ratio = balance / capital;
    if (ratio <= 0) return null;
    return (Math.pow(ratio, 1 / years) - 1) * 100;
  }, [capital, balance, firstDate]);

  // Yahoo price chart — fetch on mount and on period change
  useEffect(() => {
    if (!security.ticker) return;
    setPriceLoading(true);
    setPriceData(null);
    fetchYahooPrices(security.ticker, pricePeriod).then(result => {
      setPriceData(result || false);
      setPriceLoading(false);
    });
  }, [security.ticker, pricePeriod]);

  const saveExpectedRate = async () => {
    const rate = rateInput.trim();
    for (const w of security.accounts) {
      await updateDocument('wallets', w.id, { expectedRate: rate });
    }
    setEditingRate(false);
  };

  const lastHistLabel = stackedChartData[stackedChartData.length - 1]?.label ?? null;
  const lastFuturePoint = projYears > 0 ? combinedChartData[combinedChartData.length - 1] : null;
  const lastProjTotal = lastFuturePoint?.isProjected
    ? (lastFuturePoint.capital || 0) + (lastFuturePoint.interest || 0)
    : null;

  return (
    <div className="space-y-4 pt-1">
      {/* KPI grid */}
      <div className="card p-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-blue-50/80 dark:bg-blue-900/10 p-2.5">
            <div className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase mb-0.5">Capitale</div>
            <div className="text-sm font-black text-gray-900 dark:text-gray-100">{fmt(capital)}</div>
          </div>
          <div className="rounded-xl bg-amber-50/80 dark:bg-amber-900/10 p-2.5">
            <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase mb-0.5">Interessi</div>
            <div className="text-sm font-black text-gray-900 dark:text-gray-100">{fmt(interest)}</div>
          </div>
          {grossAnnualRate != null ? (
            <>
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                <div className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">Rend. lordo</div>
                <div className="text-sm font-black text-gray-700 dark:text-gray-300">{grossAnnualRate > 0 ? `${grossAnnualRate}%/a` : 'N/D'}</div>
              </div>
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/10 p-2.5">
                <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase mb-0.5">Rend. netto</div>
                <div className="text-sm font-black text-emerald-600 dark:text-emerald-400">{grossAnnualRate > 0 ? `${(grossAnnualRate * 0.74).toFixed(2)}%/a` : 'N/D'}</div>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                <div className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">Rend. effettivo</div>
                <div className={`text-sm font-black ${effectiveReturn !== null ? (effectiveReturn >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500') : 'text-gray-400'}`}>
                  {effectiveReturn !== null ? `${effectiveReturn >= 0 ? '+' : ''}${effectiveReturn.toFixed(2)}%/a` : 'N/D'}
                </div>
              </div>
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                <div className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">Rend. atteso</div>
                <div className="mt-0.5">
                  {editingRate ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={rateInput}
                        onChange={e => setRateInput(e.target.value)}
                        className="w-16 text-xs font-black rounded-lg border border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-gray-900 dark:text-gray-100"
                        autoFocus
                      />
                      <button onClick={saveExpectedRate} className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 text-[11px] font-bold">✓</button>
                      <button onClick={() => setEditingRate(false)} className="text-gray-400 hover:text-gray-600 text-[11px]">✕</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-sm font-black ${security.expectedRate ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`}>
                        {security.expectedRate ? `${security.expectedRate}%/a` : 'N/D'}
                      </span>
                      <button onClick={() => { setRateInput(String(security.expectedRate || '')); setEditingRate(true); }} className="text-gray-400 hover:text-blue-500">
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Combined chart: Capital vs Interest + optional projection line */}
      {combinedChartData.length > 1 && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase font-bold tracking-widest text-gray-500">
              Andamento{projYears > 0 && parseFloat(security.expectedRate) > 0 ? ' + Proiezione' : ''}
            </span>
            <div className="flex bg-gray-100 dark:bg-gray-800/80 p-0.5 rounded-lg">
              {['6M', '12M', 'MAX'].map(p => (
                <button key={p} onClick={() => setChartPeriod(p)}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold transition-all ${chartPeriod === p ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500'}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={combinedChartData} margin={{ top: 5, right: 5, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id={`scap-${safeId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id={`sint-${safeId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/><stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.15} />
                <XAxis dataKey="label" tick={false} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={42} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  const cap = d?.capital ?? 0, int = d?.interest ?? 0;
                  return (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-2.5 text-xs min-w-[160px] space-y-1">
                      <p className="font-bold text-gray-400 uppercase text-[10px]">
                        {d?.tooltipLabel || d?.label}{d?.isProjected ? ' · stima' : ''}
                      </p>
                      <div className="flex justify-between gap-3"><span className="text-blue-500">Capitale:</span><span className="font-bold">{fmt(cap)}</span></div>
                      <div className="flex justify-between gap-3"><span className="text-amber-500">Interessi:</span><span className="font-bold">{fmt(int)}</span></div>
                      <div className="border-t border-gray-100 dark:border-gray-700 pt-1 flex justify-between gap-3 font-black">
                        <span>Totale:</span><span>{fmt(cap + int)}</span>
                      </div>
                    </div>
                  );
                }} />
                {/* Vertical marker at boundary between historical and projection */}
                {projYears > 0 && parseFloat(security.expectedRate) > 0 && lastHistLabel && (
                  <ReferenceLine x={lastHistLabel} stroke="#94a3b8" strokeDasharray="4 3" strokeWidth={1.5}
                    label={{ value: 'oggi', position: 'insideTopRight', fontSize: 8, fill: '#94a3b8', dy: -2 }} />
                )}
                <Area type="monotone" dataKey="capital" name="Capitale" stackId="1" stroke="#3b82f6" fill={`url(#scap-${safeId})`} strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="interest" name="Interessi" stackId="1" stroke="#f59e0b" fill={`url(#sint-${safeId})`} strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-1 justify-center flex-wrap">
            <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Capitale</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Interessi</span>
          </div>
          {/* Projection controls below chart */}
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-gray-500 uppercase flex-shrink-0">Proiezione:</span>
              <div className="flex bg-gray-100 dark:bg-gray-800/80 p-0.5 rounded-lg flex-1">
                {[0, 5, 10, 20, 30].map(y => (
                  <button key={y} onClick={() => setSecProjYears(y)}
                    className={`flex-1 py-0.5 rounded-md text-[10px] font-extrabold transition-all ${projYears === y ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500'}`}>
                    {y === 0 ? 'No' : `${y}a`}
                  </button>
                ))}
              </div>
            </div>
            {projYears > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setProjMode(m => m === 'lump' ? 'pac' : 'lump')}
                  title={projMode === 'lump' ? 'Investimento unico — clicca per PAC' : 'PAC mensile — clicca per Lump Sum'}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-black bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800/60 min-w-[52px] text-center select-none transition-all"
                >
                  {projMode === 'lump' ? 'LUMP' : 'PAC'}
                </button>
                <input
                  type="number" min="0" step={projMode === 'lump' ? 100 : 50}
                  value={projAmount || ''}
                  onChange={e => setProjAmount(Number(e.target.value) || 0)}
                  placeholder={projMode === 'lump' ? 'Importo unico (€)' : 'Rata mensile (€)'}
                  className="input text-sm flex-1"
                />
                {lastProjTotal != null && lastProjTotal > 0 && (
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] text-gray-500">fra {projYears}a</div>
                    <div className="text-xs font-black" style={{ color }}>{fmtShort(lastProjTotal)}</div>
                  </div>
                )}
              </div>
            )}
            {projYears > 0 && !parseFloat(security.expectedRate) && (
              <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> Imposta il rendimento atteso per la proiezione
              </div>
            )}
          </div>
        </div>
      )}

      {/* Yahoo Finance price chart — hidden if no data for this ticker */}
      {security.ticker && priceData !== false && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase font-bold tracking-widest text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5" /> Andamento Prezzo
            </span>
            <div className="flex items-center gap-2">
              {priceLoading && <RefreshCw className="w-3 h-3 text-amber-500 animate-spin" />}
              <div className="flex bg-gray-100 dark:bg-gray-800/80 p-0.5 rounded-lg">
                {['6M', '12M', 'MAX'].map(p => (
                  <button key={p} onClick={() => setPricePeriod(p)}
                    className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold transition-all ${pricePeriod === p ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {priceLoading && (
            <div className="h-[140px] flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-gray-300 animate-spin" />
            </div>
          )}
          {!priceLoading && priceData?.data?.length > 1 && (
            <div style={{ height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={priceData.data} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`yp-${safeId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.35}/><stop offset="95%" stopColor={color} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.1} />
                  <XAxis dataKey="label" tick={{ fontSize: 8, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis hide domain={['auto', 'auto']} />
                  <Tooltip
                    formatter={(v) => [`${priceData.currency}${typeof v === 'number' ? v.toFixed(2) : v}`, 'Prezzo']}
                    labelStyle={{ fontSize: 10 }} contentStyle={{ fontSize: 10, padding: '4px 8px', borderRadius: 8 }} />
                  <Area type="monotone" dataKey="close" stroke={color} fill={`url(#yp-${safeId})`} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SecuritySection — groups wallets by ticker/name, shows list + detail panel
// ─────────────────────────────────────────────────────────────────────────────
const SecuritySection = ({ wallets, allWallets, incomes, expenses, portfolioValue, totalPatrimony, updateDocument, sectionColor }) => {
  const [selectedKey, setSelectedKey] = useState(null);

  const securities = useMemo(() => {
    const map = {};
    wallets.forEach(w => {
      const ticker = (w.isinCode || '').trim();
      const key = (ticker || w.name || w.id).toLowerCase().replace(/\s+/g, '-');
      if (!map[key]) {
        map[key] = {
          key,
          ticker,
          name: w.isinName || w.name || ticker || 'Titolo',
          color: w.color || sectionColor || '#6366f1',
          accounts: [],
        };
      }
      map[key].accounts.push(w);
    });
    return Object.values(map).map(sec => {
      const bal = sec.accounts.reduce((s, w) => s + (w.balance || 0), 0);
      const firstWithRate = sec.accounts.find(w => w.expectedRate && parseFloat(w.expectedRate) > 0);
      const expectedRate = firstWithRate ? parseFloat(firstWithRate.expectedRate) : '';
      return { ...sec, balance: bal, expectedRate };
    }).sort((a, b) => b.balance - a.balance);
  }, [wallets, sectionColor]);

  const accentColor = sectionColor || '#6366f1';

  const generalSecurity = useMemo(() => {
    const totalBal = securities.reduce((s, sec) => s + sec.balance, 0);
    const weightedRate = totalBal > 0
      ? securities.reduce((s, sec) => s + sec.balance * (parseFloat(sec.expectedRate) || 0), 0) / totalBal
      : 0;
    return {
      key: '__general__',
      ticker: '',
      name: 'Generale',
      color: accentColor,
      accounts: allWallets || wallets,
      balance: totalBal,
      expectedRate: weightedRate > 0 ? weightedRate.toFixed(2) : '',
    };
  }, [securities, wallets, allWallets, accentColor]);

  const allItems = useMemo(() => [generalSecurity, ...securities], [generalSecurity, securities]);

  return (
    <div className="space-y-2">
      {allItems.map(sec => (
        <div key={sec.key}>
          <button
            onClick={() => setSelectedKey(selectedKey === sec.key ? null : sec.key)}
            className={`w-full card overflow-hidden text-left transition-all duration-200 ${
              selectedKey === sec.key
                ? 'ring-2 shadow-lg'
                : 'hover:shadow-md hover:scale-[1.01] active:scale-[0.99]'
            }`}
            style={{
              ringColor: accentColor,
              borderTop: `3px solid ${accentColor}`,
            }}
          >
            {/* Colored header band */}
            <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-3"
              style={{ background: `${accentColor}12` }}
            >
              <div className="min-w-0">
                <div className="text-lg font-black font-mono tracking-tight leading-none"
                  style={{ color: accentColor }}>
                  {sec.ticker || sec.name}
                </div>
                {sec.ticker && (
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-0.5 truncate max-w-[180px]">
                    {sec.name}
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0 flex items-center gap-2">
                <div className="font-black text-base text-gray-900 dark:text-gray-100">{fmt(sec.balance)}</div>
                {selectedKey === sec.key
                  ? <ChevronUp className="w-4 h-4 text-gray-400" />
                  : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </div>
            {/* Detail chips */}
            <div className="px-4 py-2 flex justify-center items-center gap-3 flex-wrap border-t border-gray-100 dark:border-gray-800">
              <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                <span className="font-black text-gray-700 dark:text-gray-200">{pct(sec.balance, portfolioValue)}%</span> portafoglio
              </span>
              <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                <span className="font-black text-gray-700 dark:text-gray-200">{pct(sec.balance, totalPatrimony)}%</span> patrimonio
              </span>
              <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                <span className="font-black text-gray-700 dark:text-gray-200">{sec.accounts.length}</span> {sec.accounts.length === 1 ? 'conto' : 'conti'}
              </span>
            </div>
          </button>
          {selectedKey === sec.key && (
            <div className="mt-2 animate-fade-in-up">
              <SecurityDetail
                security={sec}
                incomes={incomes}
                expenses={expenses}
                portfolioValue={portfolioValue}
                updateDocument={updateDocument}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-security card with Yahoo fetch
// ─────────────────────────────────────────────────────────────────────────────
const SecurityCard = ({ wallet, portfolioValue, walletFlows, allExpenses, allIncomes }) => {
  const [yahooData, setYahooData] = useState(null);
  const [yahooLoading, setYahooLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const type  = classifyWallet(wallet);
  const color = wallet.color || TYPE_COLOR[type] || '#888';
  const flow  = walletFlows[wallet.id] || { in: 0, out: 0 };
  const balance = wallet.balance || 0;
  const netFlow = flow.in - flow.out;

  // Gain estimate vs invested (transfers = capital)
  const investedCapital = flow.in;
  const unrealizedGain  = balance - investedCapital + flow.out;
  const gainPct = investedCapital > 0 ? (unrealizedGain / investedCapital * 100).toFixed(2) : null;

  // Monthly balance trend for THIS wallet (last 12 months)
  const monthlyTrend = useMemo(() => {
    const wid = wallet.id;
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = subMonths(new Date(), i);
      const start = startOfMonth(d);
      const end   = endOfMonth(d);
      const label = format(d, "MMM ''yy", { locale: it });
      const inc = (allIncomes || []).filter(tx => tx.walletId === wid && tx.date && new Date(tx.date) >= start && new Date(tx.date) <= end).reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
      const exp = (allExpenses || []).filter(tx => tx.walletId === wid && tx.date && new Date(tx.date) >= start && new Date(tx.date) <= end).reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
      months.push({ label, inc, exp, net: inc - exp });
    }
    // cumulative
    let cum = 0;
    return months.map(m => { cum += m.net; return { ...m, capital: cum - m.net, total: cum }; });
  }, [wallet.id, allIncomes, allExpenses]);

  const fetchYahoo = useCallback(async () => {
    if (!wallet.isinCode || yahooLoading || yahooData !== null) return;
    setYahooLoading(true);
    const data = await fetchYahooCagr(wallet.isinCode);
    setYahooData(data ?? false);
    setYahooLoading(false);
  }, [wallet.isinCode, yahooLoading, yahooData]);

  // Auto-fetch on mount if ticker is present
  useEffect(() => {
    if (wallet.isinCode && yahooData === null) {
      fetchYahoo();
    }
  }, [wallet.isinCode]);

  // Projected growth at CAGR (10 years)
  const projectedGrowth = useMemo(() => {
    if (!yahooData || yahooData === false || yahooData.cagr === undefined) return [];
    const years = 10;
    const rate  = yahooData.cagr / 100;
    return Array.from({ length: years + 1 }, (_, i) => ({
      label: i === 0 ? 'Oggi' : `+${i}a`,
      projected: parseFloat((balance * Math.pow(1 + rate, i)).toFixed(2)),
      base: balance,
    }));
  }, [yahooData, balance]);

  return (
    <div className="card overflow-hidden" style={{ borderLeft: `3px solid ${color}` }}>
      {/* Header row */}
      <button
        className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: `${color}12` }}>
          {TYPE_ICON[type]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-900 dark:text-gray-100 truncate">{wallet.name}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0" style={{ background: `${color}18`, color }}>
              {TYPE_LABEL[type]}
            </span>
          </div>
          {wallet.isinCode && (
            <div className="text-[10px] font-mono text-gray-400 truncate">
              {wallet.isinCode}{wallet.isinName ? ` · ${wallet.isinName}` : ''}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-base font-black text-gray-900 dark:text-gray-100">{fmt(balance)}</div>
          {gainPct !== null && (
            <div className={`text-[11px] font-bold ${parseFloat(gainPct) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {parseFloat(gainPct) >= 0 ? '+' : ''}{gainPct}% stimato
            </div>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-5 space-y-4 border-t border-gray-100 dark:border-gray-700/50 pt-4">
          {/* KPI grid */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { l: 'Saldo attuale',      v: fmt(balance),    c: 'bg-gray-50 dark:bg-gray-800/50' },
              { l: 'Peso portafoglio',   v: `${pct(balance, portfolioValue)}%`, c: 'bg-gray-50 dark:bg-gray-800/50' },
              { l: 'Entrate totali',     v: fmt(flow.in),    c: 'bg-emerald-50/80 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400' },
              { l: 'Uscite totali',      v: fmt(flow.out),   c: 'bg-red-50/80 dark:bg-red-900/10 text-red-600 dark:text-red-400' },
            ].map(({ l, v, c }) => (
              <div key={l} className={`rounded-xl p-2.5 ${c}`}>
                <div className="text-[10px] uppercase font-bold text-gray-500 mb-0.5">{l}</div>
                <div className="text-sm font-black">{v}</div>
              </div>
            ))}
          </div>

          {/* ISIN details */}
          {(wallet.isinCode || wallet.isinName) && (
            <div className="rounded-xl p-3 bg-blue-50/60 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 space-y-1">
              <div className="text-[10px] font-bold text-blue-700 dark:text-blue-400 flex items-center gap-1.5 mb-1">
                <Info className="w-3.5 h-3.5" /> Dettagli Titolo
              </div>
              {wallet.isinName && <div className="text-xs text-gray-800 dark:text-gray-200 font-semibold">{wallet.isinName}</div>}
              {wallet.isinCode && <div className="text-[10px] font-mono text-blue-600 dark:text-blue-300">{wallet.isinCode}</div>}
            </div>
          )}

          {/* Monthly trend chart (Statistics-style: capital dashed + total solid) */}
          {monthlyTrend.some(m => m.total !== 0) && (
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-2">Andamento flussi (12 mesi)</div>
              <div style={{ height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyTrend} margin={{ top: 5, right: 5, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`grad-sec-${wallet.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.15} />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={42} domain={['auto', 'auto']} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2.5 min-w-[110px]">
                          <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{d?.label}</p>
                          <div className="space-y-0.5">
                            <p className="text-xs text-gray-400 flex justify-between gap-4"><span>Capitale:</span><span>{fmtShort(d?.capital)}</span></p>
                            <p className="text-xs font-bold flex justify-between gap-4" style={{ color }}><span>Valore:</span><span>{fmtShort(d?.total)}</span></p>
                          </div>
                        </div>
                      );
                    }} />
                    {/* Capital line: dashed gray */}
                    <Area type="monotone" dataKey="capital" name="Capitale" stroke="#94a3b8" fill="transparent" strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 4, fill: '#94a3b8' }} />
                    {/* Total line: solid colored */}
                    <Area type="monotone" dataKey="total" name="Valore" stroke={color} fill={`url(#grad-sec-${wallet.id})`} strokeWidth={3} dot={{ r: 3, fill: color, strokeWidth: 0 }} activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Yahoo Finance section */}
          {wallet.isinCode && (
            <div className="rounded-xl border border-amber-200/60 dark:border-amber-800/30 bg-amber-50/60 dark:bg-amber-900/10 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5" /> Performance Storica (Yahoo Finance)
                </div>
                {yahooLoading && <RefreshCw className="w-3.5 h-3.5 text-amber-500 animate-spin" />}
                {!yahooLoading && yahooData === null && (
                  <button onClick={fetchYahoo} className="text-[10px] font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Carica
                  </button>
                )}
              </div>

              {yahooData === false && (
                <p className="text-[11px] text-gray-500">Dati non disponibili per questo ticker.</p>
              )}

              {yahooData && yahooData !== false && (
                <>
                  {/* CAGR badges */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[10px] text-gray-500 font-semibold">CAGR {yahooData.years.toFixed(0)}a</div>
                      <div className={`text-base font-black ${yahooData.cagr >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                        {yahooData.cagr >= 0 ? '+' : ''}{yahooData.cagr}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 font-semibold">Prezzo iniziale</div>
                      <div className="text-sm font-black text-gray-800 dark:text-gray-200">
                        {yahooData.currency}{yahooData.firstClose?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 font-semibold">Prezzo attuale</div>
                      <div className="text-sm font-black text-gray-800 dark:text-gray-200">
                        {yahooData.currency}{yahooData.lastClose?.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Sparkline: historical close */}
                  {yahooData.sparkline?.length > 2 && (
                    <div>
                      <div className="text-[10px] text-gray-500 font-semibold mb-1">Storico prezzi (ultimi 5 anni)</div>
                      <div style={{ height: 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={yahooData.sparkline} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`spark-${wallet.id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="label" tick={{ fontSize: 8 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                            <YAxis hide domain={['auto', 'auto']} />
                            <Tooltip formatter={(v) => [`${yahooData.currency}${v?.toFixed(2)}`, 'Prezzo']} labelStyle={{ fontSize: 10 }} contentStyle={{ fontSize: 10, padding: '4px 8px', borderRadius: 8 }} />
                            <Area type="monotone" dataKey="close" name="Prezzo" stroke="#f59e0b" fill={`url(#spark-${wallet.id})`} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Projected growth chart */}
                  {projectedGrowth.length > 1 && (
                    <div>
                      <div className="text-[10px] text-gray-500 font-semibold mb-1">
                        Proiezione +10 anni al CAGR {yahooData.cagr}%/a
                      </div>
                      <div style={{ height: 100 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={projectedGrowth} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`proj-${wallet.id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor={color} stopOpacity={0.4} />
                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="label" tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
                            <YAxis hide domain={['auto', 'auto']} />
                            <Tooltip formatter={(v) => [fmtShort(v), 'Proiez.']} labelStyle={{ fontSize: 10 }} contentStyle={{ fontSize: 10, padding: '4px 8px', borderRadius: 8 }} />
                            <Area type="monotone" dataKey="base"      name="Capitale"   stroke="#94a3b8" fill="transparent" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                            <Area type="monotone" dataKey="projected" name="Proiez."    stroke={color}   fill={`url(#proj-${wallet.id})`} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: color }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="text-[10px] text-gray-400 text-center mt-1">
                        Valore stimato fra 10 anni: <strong className="font-black" style={{ color }}>{fmt(projectedGrowth[projectedGrowth.length - 1]?.projected ?? 0)}</strong>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Deposit section — one tile per account, expands to SecurityDetail + rate extras
// ─────────────────────────────────────────────────────────────────────────────
const DepositSection = ({ wallets, allWallets, incomes, expenses, portfolioValue, totalPatrimony, updateDocument }) => {
  const [selectedId, setSelectedId] = useState(null);

  const securities = useMemo(() => wallets.map(w => ({
    key: w.id,
    ticker: w.isinCode || '',
    name: w.isinName || w.name || 'Conto Deposito',
    color: w.color || TYPE_COLOR.deposito,
    accounts: [w],
    balance: w.balance || 0,
    // Use saved expectedRate if set, otherwise default to net rate from grossAnnualRate
    expectedRate: w.expectedRate || (w.grossAnnualRate ? (parseFloat(w.grossAnnualRate) * 0.74).toFixed(2) : ''),
    grossAnnualRate: parseFloat(w.grossAnnualRate) || 0,
  })), [wallets]);

  const generalDeposit = useMemo(() => {
    const totalBal = securities.reduce((s, sec) => s + sec.balance, 0);
    const weightedGross = totalBal > 0
      ? securities.reduce((s, sec) => s + sec.balance * sec.grossAnnualRate, 0) / totalBal
      : 0;
    return {
      key: '__general__',
      ticker: '',
      name: 'Generale',
      color: TYPE_COLOR.deposito,
      accounts: allWallets || wallets,
      balance: totalBal,
      expectedRate: weightedGross > 0 ? (weightedGross * 0.74).toFixed(2) : '',
      grossAnnualRate: weightedGross,
    };
  }, [securities, wallets, allWallets]);

  const allDeposits = useMemo(() => [generalDeposit, ...securities], [generalDeposit, securities]);

  return (
    <div className="space-y-2">
      {allDeposits.map(sec => {
        const rate = sec.grossAnnualRate;
        const netRate = rate * 0.74;
        const gross = sec.balance * rate / 100;
        const net = gross * 0.74;
        const netMonthly = net / 12;
        const accentColor = sec.color;

        return (
          <div key={sec.key}>
            <button
              onClick={() => setSelectedId(selectedId === sec.key ? null : sec.key)}
              className={`w-full card overflow-hidden text-left transition-all duration-200 ${
                selectedId === sec.key
                  ? 'ring-2 shadow-lg'
                  : 'hover:shadow-md hover:scale-[1.01] active:scale-[0.99]'
              }`}
              style={{ borderTop: `3px solid ${accentColor}` }}
            >
              <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-3"
                style={{ background: `${accentColor}12` }}
              >
                <div className="min-w-0">
                  <div className="text-lg font-black font-mono tracking-tight leading-none truncate"
                    style={{ color: accentColor }}>
                    {sec.ticker || sec.name}
                  </div>
                  {sec.ticker && (
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-0.5 truncate max-w-[180px]">
                      {sec.name}
                    </div>
                  )}
                  {!sec.ticker && (
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-0.5">
                      Conto Deposito
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="font-black text-base text-gray-900 dark:text-gray-100">{fmt(sec.balance)}</div>
                  {selectedId === sec.key
                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                    : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>
              <div className="px-4 py-2 flex justify-center items-center gap-3 flex-wrap border-t border-gray-100 dark:border-gray-800">
                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                  <span className="font-black text-gray-700 dark:text-gray-200">{pct(sec.balance, portfolioValue)}%</span> portafoglio
                </span>
                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                  <span className="font-black text-gray-700 dark:text-gray-200">{pct(sec.balance, totalPatrimony)}%</span> patrimonio
                </span>
              </div>
            </button>

            {selectedId === sec.key && (
              <div className="mt-2 animate-fade-in-up space-y-3">
                <SecurityDetail
                  security={sec}
                  incomes={incomes}
                  expenses={expenses}
                  portfolioValue={portfolioValue}
                  updateDocument={updateDocument}
                  grossAnnualRate={rate}
                />
                {/* Deposit-specific: rate breakdown */}
                <div className="card p-4 space-y-3">
                  <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400">Rendimento Deposito</div>
                  {rate > 0 ? (
                    <>
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                          <div className="text-[10px] text-gray-500 font-semibold">Lordo/anno</div>
                          <div className="text-sm font-black text-gray-700 dark:text-gray-300">{fmt(gross)}</div>
                          <div className="text-[9px] text-gray-400">{rate}%</div>
                        </div>
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/10 p-2.5">
                          <div className="text-[10px] text-gray-500 font-semibold">Netto/anno</div>
                          <div className="text-sm font-black text-emerald-600 dark:text-emerald-400">{fmt(net)}</div>
                          <div className="text-[9px] text-gray-400">{netRate.toFixed(2)}%</div>
                        </div>
                      </div>
                      <div className="rounded-xl bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800/30 px-3 py-2.5 flex items-center justify-between">
                        <span className="text-[11px] text-gray-500 font-semibold">Rata mensile netta</span>
                        <span className="text-base font-black text-emerald-600 dark:text-emerald-400">{fmt(netMonthly)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-gray-400 italic text-center py-2">
                      Nessun tasso configurato. Modifica il conto per aggiungere il tasso lordo annuo.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
export default function InvestmentDashboard({ wallets = [], expenses = [], incomes = [], debts = [], investmentPlans = [], addDocument, updateDocument, deleteDocument, userSettings = {}, onUpdateSettings }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [overviewTimeframe, setOverviewTimeframe] = useState(() => {
    try { return localStorage.getItem('inv_overview_timeframe') || '12M'; } catch { return '12M'; }
  });

  const [cagrMap, setCagrMap] = useState({});

  const LS_KEY = 'inv_simulation_v2';
  const loadLS = () => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
  };
  const DEFAULT_CONFIG = { immediateChange: 0, phases: [{ amount: 0, endYear: 2100 }] };
  const currentYear = new Date().getFullYear();

  const [projYears, setProjYears] = useState(() => {
    const s = loadLS(); return s.projYears ?? 10;
  });
  const [etfConfig, setEtfConfig] = useState(() => {
    const s = loadLS(); return s.etfConfig ?? DEFAULT_CONFIG;
  });
  const [azioniConfig, setAzioniConfig] = useState(() => {
    const s = loadLS(); return s.azioniConfig ?? DEFAULT_CONFIG;
  });
  const [obblConfig, setObblConfig] = useState(() => {
    const s = loadLS(); return s.obblConfig ?? DEFAULT_CONFIG;
  });
  const [depositoConfig, setDepositoConfig] = useState(() => {
    const s = loadLS(); return s.depositoConfig ?? DEFAULT_CONFIG;
  });

  // Persist simulation settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ projYears, etfConfig, azioniConfig, obblConfig, depositoConfig }));
    } catch {}
  }, [projYears, etfConfig, azioniConfig, obblConfig, depositoConfig]);

  const [activeModalConfig, setActiveModalConfig] = useState(null); // 'etf' | 'azioni' | 'obbligazioni' | 'deposito' | null
  const [tempConfig, setTempConfig] = useState(null);

  // Investment plans
  const [activePlanId, setActivePlanId] = useState(() => {
    try { return localStorage.getItem('inv_active_plan_id') || null; } catch { return null; }
  });
  const [showPlanModal, setShowPlanModal] = useState(null); // 'save' | 'rename' | null
  const [planNameInput, setPlanNameInput] = useState('');
  const [planSaving, setPlanSaving] = useState(false);

  // Comparison mode — 'current' = configurazione attuale, plan.id = piano salvato
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedComparisonIds, setSelectedComparisonIds] = useState([]);

  // Persist overview timeframe and active plan to localStorage
  useEffect(() => {
    try { localStorage.setItem('inv_overview_timeframe', overviewTimeframe); } catch {}
  }, [overviewTimeframe]);

  useEffect(() => {
    try {
      if (activePlanId) localStorage.setItem('inv_active_plan_id', activePlanId);
      else localStorage.removeItem('inv_active_plan_id');
    } catch {}
  }, [activePlanId]);





  // ── Stacked Monthly Trend Calculations for Overview ───────────────────────
  // ── Stacked Weekly Trend Calculations for Overview ────────────────────────
  // ── Reference date: end of the previous month (excludes current month completely) ──
  const now = useMemo(() => endOfMonth(subMonths(new Date(), 1)), []);

  // Reconstruct wallet balances exactly as of the end of the previous month by walking backward
  const walletsAsOfPreviousMonth = useMemo(() => {
    return wallets.map(w => {
      if (w.isVirtual) return w;
      let balance = Number(w.balance) || 0;

      // Walk backward through incomes in the current month (after now)
      incomes.forEach(tx => {
        if (tx.walletId === w.id && tx.date) {
          const txDate = new Date(tx.date);
          if (txDate > now) {
            balance -= Number(tx.amount) || 0;
          }
        }
      });

      // Walk backward through expenses in the current month (after now)
      expenses.forEach(tx => {
        if (tx.walletId === w.id && tx.date) {
          const txDate = new Date(tx.date);
          if (txDate > now) {
            balance += Number(tx.amount) || 0;
          }
        }
      });

      // Walk backward through debts in the current month (after now)
      debts.forEach(debt => {
        if (debt.walletId === w.id && debt.date) {
          const debtDate = new Date(debt.date);
          if (debtDate > now) {
            const amount = Number(debt.amount) || 0;
            if (debt.type === 'credit') {
              balance += amount;
            } else {
              balance -= amount;
            }
          }
        }
      });

      return { ...w, balance };
    });
  }, [wallets, incomes, expenses, debts, now]);

  // ── Stacked Weekly Trend Calculations for Overview ────────────────────────
  const stackedWeeklyTrend = useMemo(() => {
    let startDate;
    if (overviewTimeframe === '6M') {
      startDate = startOfMonth(subMonths(now, 5));
    } else if (overviewTimeframe === '12M') {
      startDate = startOfMonth(subMonths(now, 11));
    } else { // 'MAX'
      let earliest = now;
      expenses.forEach(tx => {
        if (tx.date) {
          const d = new Date(tx.date);
          if (d < earliest) earliest = d;
        }
      });
      incomes.forEach(tx => {
        if (tx.date) {
          const d = new Date(tx.date);
          if (d < earliest) earliest = d;
        }
      });
      startDate = startOfMonth(earliest);
    }

    // Generate all weeks in the interval
    const weeks = eachWeekOfInterval({ start: startDate, end: now }, { weekStartsOn: 1 });

    let lastMonthKey = null;
    let weekIndexInMonth = 1;

    return weeks.map(wk => {
      const weekEnd = endOfWeek(wk, { weekStartsOn: 1 });
      const monthKey = format(weekEnd, 'yyyy-MM');
      
      // Make X-Axis labels completely unique to prevent Recharts category collisions/overlaps
      let xAxisLabel = "";
      if (lastMonthKey === null || monthKey !== lastMonthKey) {
        xAxisLabel = format(weekEnd, "MMM ''yy", { locale: it });
        lastMonthKey = monthKey;
        weekIndexInMonth = 1;
      } else {
        weekIndexInMonth++;
        xAxisLabel = `${format(weekEnd, "MMM ''yy", { locale: it })}_w${weekIndexInMonth}`;
      }
      
      const tooltipLabel = format(weekEnd, "d MMM yyyy", { locale: it });
      
      // Target date is either the end of the week, or now if the week is in the future
      const targetDate = weekEnd > now ? now : weekEnd;

      let liquidita = 0;
      let deposito = 0;
      let titoli = 0;

      wallets.forEach(w => {
        if (w.isVirtual) return;

        let balance = Number(w.balance) || 0;

        // Walk backward through incomes that happened after targetDate
        incomes.forEach(tx => {
          if (tx.walletId === w.id && tx.date) {
            const txDate = new Date(tx.date);
            if (txDate <= now && txDate > targetDate) {
              balance -= Number(tx.amount) || 0;
            }
          }
        });

        // Walk backward through expenses that happened after targetDate
        expenses.forEach(tx => {
          if (tx.walletId === w.id && tx.date) {
            const txDate = new Date(tx.date);
            if (txDate <= now && txDate > targetDate) {
              balance += Number(tx.amount) || 0;
            }
          }
        });

        // Walk backward through debts that happened after targetDate
        debts.forEach(debt => {
          if (debt.walletId === w.id && debt.date) {
            const debtDate = new Date(debt.date);
            if (debtDate <= now && debtDate > targetDate) {
              const amount = Number(debt.amount) || 0;
              if (debt.type === 'credit') {
                balance += amount;
              } else {
                balance -= amount;
              }
            }
          }
        });

        const type = classifyWallet(w);
        if (type === null) {
          liquidita += balance;
        } else if (type === 'deposito') {
          deposito += balance;
        } else if (type === 'etf' || type === 'azioni' || type === 'obbligazioni') {
          titoli += balance;
        }
      });

      // Cumulative incomes of category 'Investimenti' across ALL accounts up to targetDate
      let cumulativeIncomes = 0;
      incomes.forEach(tx => {
        if (tx.date && tx.category === 'Investimenti') {
          const txDate = new Date(tx.date);
          if (txDate <= targetDate) {
            cumulativeIncomes += Number(tx.amount) || 0;
          }
        }
      });

      // Cumulative expenses of category 'Investimenti' across ALL accounts up to targetDate
      let cumulativeExpenses = 0;
      expenses.forEach(tx => {
        if (tx.date && tx.category === 'Investimenti') {
          const txDate = new Date(tx.date);
          if (txDate <= targetDate) {
            cumulativeExpenses += Number(tx.amount) || 0;
          }
        }
      });

      // Base net interests: cumulative incomes minus cumulative expenses, bounded at 0
      const baseInteressi = Math.max(0, cumulativeIncomes - cumulativeExpenses);

      // Clamped net interests: cannot exceed the actual total balance of the securities wallets
      // This prevents negative 'titoli' values when market drops are severe, avoiding visual artifacts in the chart,
      // while preserving perfect stacked total consistency.
      const netInteressi = Math.max(0, Math.min(titoli, baseInteressi));

      // Net security balances without interests, guaranteed to be non-negative unless titoli itself is negative
      // If cumulativeExpenses > cumulativeIncomes, the excess expense automatically reduces titoli balance,
      // and when cumulativeIncomes later increases, it first heals the netTitoli balance back to invested capital.
      const netTitoli = titoli - netInteressi;

      return {
        label: xAxisLabel,
        tooltipLabel,
        liquidita: Number(liquidita.toFixed(2)),
        deposito: Number(deposito.toFixed(2)),
        titoli: Number(netTitoli.toFixed(2)),
        interessi: Number(netInteressi.toFixed(2)),
        total: Number((liquidita + deposito + titoli).toFixed(2))
      };
    });
  }, [wallets, expenses, incomes, debts, overviewTimeframe, now]);

  // ── Wallet groups (ignore 0-balance) based on previous month end ──────────
  const securityWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => isSecurityWallet(w) && (w.balance || 0) !== 0), [walletsAsOfPreviousMonth]);
  const depositWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => isDepositWallet(w) && Math.abs(w.balance || 0) >= 0.01), [walletsAsOfPreviousMonth]);
  const liquidWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => isLiquidWallet(w) && (w.balance || 0) !== 0), [walletsAsOfPreviousMonth]);

  // Per-type groups for dedicated sections
  const etfWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'etf' && (w.balance || 0) !== 0), [walletsAsOfPreviousMonth]);
  const azioniWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'azioni' && (w.balance || 0) !== 0), [walletsAsOfPreviousMonth]);
  const obbligazioniWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'obbligazioni' && (w.balance || 0) !== 0), [walletsAsOfPreviousMonth]);

  // Unfiltered versions for Generale tile (includes zero-balance accounts with historical data)
  const allEtfWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'etf'), [walletsAsOfPreviousMonth]);
  const allAzioniWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'azioni'), [walletsAsOfPreviousMonth]);
  const allObbligazioniWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'obbligazioni'), [walletsAsOfPreviousMonth]);
  const allDepositWallets = useMemo(() =>
    walletsAsOfPreviousMonth.filter(w => isDepositWallet(w)), [walletsAsOfPreviousMonth]);

  const allInvestmentWallets = [...securityWallets, ...depositWallets];

  // Pre-fetch CAGRs at parent level to calculate actual yield and future projection
  useEffect(() => {
    securityWallets.forEach(async (w) => {
      if (w.isinCode && cagrMap[w.isinCode] === undefined) {
        try {
          const data = await fetchYahooCagr(w.isinCode);
          if (data && typeof data.cagr === 'number') {
            setCagrMap(prev => ({ ...prev, [w.isinCode]: data.cagr }));
          }
        } catch (e) {
          console.error("Errore nel caricamento CAGR per", w.isinCode, e);
        }
      }
    });
  }, [securityWallets, cagrMap]);


  // ── Portfolio KPIs based on previous month end ────────────────────────────
  const securityValue = useMemo(() => securityWallets.reduce((s, w) => s + (w.balance || 0), 0), [securityWallets]);
  const depositValue  = useMemo(() => depositWallets.reduce((s, w) => s + (w.balance || 0), 0), [depositWallets]);
  const liquidValue   = useMemo(() => liquidWallets.reduce((s, w) => s + (w.balance || 0), 0), [liquidWallets]);
  const portfolioValue = securityValue + depositValue;
  const totalPatrimony = portfolioValue + liquidValue;

  const liquidPct = totalPatrimony > 0 ? ((liquidValue / totalPatrimony) * 100).toFixed(0) : '0';
  const depositPct = totalPatrimony > 0 ? ((depositValue / totalPatrimony) * 100).toFixed(0) : '0';
  const securityPct = totalPatrimony > 0 ? ((securityValue / totalPatrimony) * 100).toFixed(0) : '0';

  const liquidPctVal = totalPatrimony > 0 ? Math.round((liquidValue / totalPatrimony) * 100) : 0;
  const depositPctVal = totalPatrimony > 0 ? Math.round((depositValue / totalPatrimony) * 100) : 0;
  const securityPctVal = totalPatrimony > 0 ? Math.round((securityValue / totalPatrimony) * 100) : 0;


  // ── New Overview KPIs & Trends (relative to now / previous month) ─────────
  const sixMonthsAgo = useMemo(() => startOfMonth(subMonths(now, 5)), [now]);
  const nineMonthsAgo = useMemo(() => startOfMonth(subMonths(now, 8)), [now]);

  const avgSavings6M = useMemo(() => {
    let income6M = 0;
    incomes.forEach(tx => {
      if (tx.date) {
        const d = new Date(tx.date);
        if (d >= sixMonthsAgo && d <= now && tx.category !== 'Trasferimento') {
          income6M += Number(tx.amount) || 0;
        }
      }
    });

    let expense6M = 0;
    expenses.forEach(tx => {
      if (tx.date) {
        const d = new Date(tx.date);
        if (d >= sixMonthsAgo && d <= now && tx.category !== 'Trasferimento') {
          expense6M += Number(tx.amount) || 0;
        }
      }
    });

    return (income6M - expense6M) / 6;
  }, [incomes, expenses, sixMonthsAgo, now]);

  const avgInvestment6M = useMemo(() => {
    let investIn6M = 0;
    incomes.forEach(tx => {
      if (tx.date && tx.category === 'Trasferimento') {
        const d = new Date(tx.date);
        if (d >= sixMonthsAgo && d <= now) {
          const wallet = walletsAsOfPreviousMonth.find(w => w.id === tx.walletId);
          if (wallet && isSecurityWallet(wallet)) {
            investIn6M += Number(tx.amount) || 0;
          }
        }
      }
    });

    let investOut6M = 0;
    expenses.forEach(tx => {
      if (tx.date && tx.category === 'Trasferimento') {
        const d = new Date(tx.date);
        if (d >= sixMonthsAgo && d <= now) {
          const wallet = walletsAsOfPreviousMonth.find(w => w.id === tx.walletId);
          if (wallet && isSecurityWallet(wallet)) {
            investOut6M += Number(tx.amount) || 0;
          }
        }
      }
    });

    return (investIn6M - investOut6M) / 6;
  }, [incomes, expenses, walletsAsOfPreviousMonth, sixMonthsAgo, now]);

  const pacAttuale = useMemo(() => {
    const lastMonthStart = startOfMonth(now);
    const lastMonthEnd = endOfMonth(now);
    let total = 0;
    incomes.forEach(tx => {
      if (tx.date) {
        const d = new Date(tx.date);
        if (d >= lastMonthStart && d <= lastMonthEnd) {
          const wallet = walletsAsOfPreviousMonth.find(w => w.id === tx.walletId);
          if (wallet && isSecurityWallet(wallet)) {
            total += Number(tx.amount) || 0;
          }
        }
      }
    });
    return total;
  }, [incomes, walletsAsOfPreviousMonth, now]);

  const emergencyFund = liquidValue + depositValue;

  const recommendedEmergencyFund = useMemo(() => {
    let expense9M = 0;
    expenses.forEach(tx => {
      if (tx.date) {
        const d = new Date(tx.date);
        if (d >= nineMonthsAgo && d <= now && tx.category !== 'Trasferimento') {
          expense9M += Number(tx.amount) || 0;
        }
      }
    });
    return expense9M;
  }, [expenses, nineMonthsAgo, now]);

  const etfValue = useMemo(() => walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'etf').reduce((s, w) => s + (w.balance || 0), 0), [walletsAsOfPreviousMonth]);
  const azioniValue = useMemo(() => walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'azioni').reduce((s, w) => s + (w.balance || 0), 0), [walletsAsOfPreviousMonth]);
  const obbligazioniValue = useMemo(() => walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'obbligazioni').reduce((s, w) => s + (w.balance || 0), 0), [walletsAsOfPreviousMonth]);

  // Expected deposit yield weighted average
  const avgExpectedDeposit = useMemo(() => {
    const sum = depositWallets.reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.grossAnnualRate) || 0), 0);
    const total = depositWallets.reduce((s, w) => s + (w.balance || 0), 0);
    return total > 0 ? sum / total : 0;
  }, [depositWallets]);

  // Weighted-average expected rate per asset class (from wallet.expectedRate, fallback to sensible defaults)
  const avgExpectedEtf = useMemo(() => {
    const wals = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'etf');
    const total = wals.reduce((s, w) => s + (w.balance || 0), 0);
    if (total === 0) return 8;
    const sum = wals.reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.expectedRate) || 8), 0);
    return sum / total;
  }, [walletsAsOfPreviousMonth]);

  const avgExpectedAzioni = useMemo(() => {
    const wals = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'azioni');
    const total = wals.reduce((s, w) => s + (w.balance || 0), 0);
    if (total === 0) return 6;
    const sum = wals.reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.expectedRate) || 6), 0);
    return sum / total;
  }, [walletsAsOfPreviousMonth]);

  const avgExpectedObbl = useMemo(() => {
    const wals = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'obbligazioni');
    const total = wals.reduce((s, w) => s + (w.balance || 0), 0);
    if (total === 0) return 4;
    const sum = wals.reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.expectedRate) || 4), 0);
    return sum / total;
  }, [walletsAsOfPreviousMonth]);

  // Total expected annual yield (weighted average across all asset classes)
  const avgExpectedTotal = useMemo(() => {
    const depositSum = depositWallets.reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.grossAnnualRate) || 0), 0);
    const etfSum = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'etf').reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.expectedRate) || 8), 0);
    const azioniSum = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'azioni').reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.expectedRate) || 6), 0);
    const obbligazioniSum = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'obbligazioni').reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.expectedRate) || 4), 0);

    const totalBal = depositValue + etfValue + azioniValue + obbligazioniValue;
    return totalBal > 0 ? (depositSum + etfSum + azioniSum + obbligazioniSum) / totalBal : 0;
  }, [depositWallets, walletsAsOfPreviousMonth, etfValue, azioniValue, obbligazioniValue, depositValue]);

  // Actual effective annual yield (Yahoo CAGR if available, then wallet.expectedRate, then hardcoded default)
  const avgEffectiveActual = useMemo(() => {
    const depositSum = depositWallets.reduce((s, w) => s + (w.balance || 0) * (parseFloat(w.grossAnnualRate) || 0), 0);
    const etfSum = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'etf').reduce((s, w) => {
      const cagr = cagrMap[w.isinCode] !== undefined ? cagrMap[w.isinCode] : (parseFloat(w.expectedRate) || 8);
      return s + (w.balance || 0) * cagr;
    }, 0);
    const azioniSum = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'azioni').reduce((s, w) => {
      const cagr = cagrMap[w.isinCode] !== undefined ? cagrMap[w.isinCode] : (parseFloat(w.expectedRate) || 6);
      return s + (w.balance || 0) * cagr;
    }, 0);
    const obbligazioniSum = walletsAsOfPreviousMonth.filter(w => classifyWallet(w) === 'obbligazioni').reduce((s, w) => {
      const cagr = cagrMap[w.isinCode] !== undefined ? cagrMap[w.isinCode] : (parseFloat(w.expectedRate) || 4);
      return s + (w.balance || 0) * cagr;
    }, 0);

    const totalBal = depositValue + etfValue + azioniValue + obbligazioniValue;
    return totalBal > 0 ? (depositSum + etfSum + azioniSum + obbligazioniSum) / totalBal : 0;
  }, [depositWallets, walletsAsOfPreviousMonth, cagrMap, etfValue, azioniValue, obbligazioniValue, depositValue]);

  // Proportional ratio for netInteressi split in the past history
  const totalSec = etfValue + azioniValue + obbligazioniValue;
  const etfRatio = totalSec > 0 ? etfValue / totalSec : 0;
  const azioniRatio = totalSec > 0 ? azioniValue / totalSec : 0;
  const obblRatio = totalSec > 0 ? obbligazioniValue / totalSec : 0;
  const totalInvestedPortfolio = (depositValue || 0) + (etfValue || 0) + (azioniValue || 0) + (obbligazioniValue || 0);

  // Extract past history using annual calendar intervals
  const historicalPastYears = useMemo(() => {
    let earliest = now;
    expenses.forEach(tx => {
      if (tx.date) {
        const d = new Date(tx.date);
        if (d < earliest) earliest = d;
      }
    });
    incomes.forEach(tx => {
      if (tx.date) {
        const d = new Date(tx.date);
        if (d < earliest) earliest = d;
      }
    });
    
    const startYear = earliest.getFullYear();
    const currentYear = now.getFullYear();
    const yearsList = [];

    // Generate annual past data points (end of each calendar year)
    for (let yr = startYear; yr < currentYear; yr++) {
      const targetDate = endOfMonth(new Date(yr, 11, 31)); // December 31st of that year

      let liquidita = 0;
      let deposito = 0;
      let titoli = 0;

      wallets.forEach(w => {
        if (w.isVirtual) return;

        let balance = Number(w.balance) || 0;

        incomes.forEach(tx => {
          if (tx.walletId === w.id && tx.date) {
            const txDate = new Date(tx.date);
            if (txDate <= now && txDate > targetDate) {
              balance -= Number(tx.amount) || 0;
            }
          }
        });

        expenses.forEach(tx => {
          if (tx.walletId === w.id && tx.date) {
            const txDate = new Date(tx.date);
            if (txDate <= now && txDate > targetDate) {
              balance += Number(tx.amount) || 0;
            }
          }
        });

        debts.forEach(debt => {
          if (debt.walletId === w.id && debt.date) {
            const debtDate = new Date(debt.date);
            if (debtDate <= now && debtDate > targetDate) {
              const amount = Number(debt.amount) || 0;
              if (debt.type === 'credit') {
                balance += amount;
              } else {
                balance -= amount;
              }
            }
          }
        });

        const type = classifyWallet(w);
        if (type === null) {
          liquidita += balance;
        } else if (type === 'deposito') {
          deposito += balance;
        } else if (type === 'etf' || type === 'azioni' || type === 'obbligazioni') {
          titoli += balance;
        }
      });

      let cumulativeIncomes = 0;
      incomes.forEach(tx => {
        if (tx.date && tx.category === 'Investimenti') {
          const txDate = new Date(tx.date);
          if (txDate <= targetDate) {
            cumulativeIncomes += Number(tx.amount) || 0;
          }
        }
      });

      let cumulativeExpenses = 0;
      expenses.forEach(tx => {
        if (tx.date && tx.category === 'Investimenti') {
          const txDate = new Date(tx.date);
          if (txDate <= targetDate) {
            cumulativeExpenses += Number(tx.amount) || 0;
          }
        }
      });

      const baseInteressi = Math.max(0, cumulativeIncomes - cumulativeExpenses);
      const netInteressi = Math.max(0, Math.min(titoli, baseInteressi));
      const netTitoli = titoli - netInteressi;

      const capit = liquidita + deposito + netTitoli;
      const int_dep = 0;
      const int_etf = netInteressi * etfRatio;
      const int_azioni = netInteressi * azioniRatio;
      const int_obbl = netInteressi * obblRatio;

      yearsList.push({
        year: String(yr),
        tooltipLabel: `Fine anno ${yr}`,
        capitale: Number(capit.toFixed(0)),
        int_dep: Number(int_dep.toFixed(0)),
        int_etf: Number(int_etf.toFixed(0)),
        int_azioni: Number(int_azioni.toFixed(0)),
        int_obbl: Number(int_obbl.toFixed(0)),
        total: Number((liquidita + deposito + titoli).toFixed(0)),
        isFuture: false
      });
    }

    return yearsList;
  }, [wallets, expenses, incomes, debts, now, etfRatio, azioniRatio, obblRatio]);


  // Helper to resolve dynamic monthly additions during future simulation
  // Phases use endYear (calendar year); unlimited if endYear is null.
  const getPhaseContribution = useCallback((config, yr) => {
    for (const phase of config.phases) {
      const amt = Number(phase.amount) || 0;
      const endYearVal = (phase.endYear === null || phase.endYear === undefined) ? 2100 : Number(phase.endYear);
      const duration = endYearVal - currentYear;
      if (duration <= 0) continue; // already past, skip
      if (yr <= duration) {
        return amt;
      }
      // This phase is over; continue to next phase
    }
    return 0;
  }, [currentYear]);

  const openConfigModal = useCallback((type) => {
    let current;
    if (type === 'etf') current = etfConfig;
    else if (type === 'azioni') current = azioniConfig;
    else if (type === 'obbligazioni') current = obblConfig;
    else current = depositoConfig;
    
    // Deep copy
    setTempConfig(JSON.parse(JSON.stringify(current)));
    setActiveModalConfig(type);
  }, [etfConfig, azioniConfig, obblConfig, depositoConfig]);

  const saveConfig = useCallback(() => {
    if (!tempConfig) return;
    
    // Remove trailing empty phases to keep data clean
    const cleanedPhases = tempConfig.phases.filter((p, idx) => {
      if (idx === tempConfig.phases.length - 1 && p.amount === 0 && (p.endYear === null || p.endYear === undefined || p.endYear === 2100)) {
        return false;
      }
      return true;
    }).map(p => ({
      amount: p.amount,
      endYear: (p.endYear === null || p.endYear === undefined) ? 2100 : p.endYear
    }));
    if (cleanedPhases.length === 0) {
      cleanedPhases.push({ amount: 0, endYear: 2100 });
    }
    const cleanedConfig = { ...tempConfig, phases: cleanedPhases };

    if (activeModalConfig === 'etf') setEtfConfig(cleanedConfig);
    else if (activeModalConfig === 'azioni') setAzioniConfig(cleanedConfig);
    else if (activeModalConfig === 'obbligazioni') setObblConfig(cleanedConfig);
    else if (activeModalConfig === 'deposito') setDepositoConfig(cleanedConfig);
    
    setActiveModalConfig(null);
    setTempConfig(null);
  }, [tempConfig, activeModalConfig]);

  // Plan helpers
  const currentPlanState = useCallback(() => ({ projYears, etfConfig, azioniConfig, obblConfig, depositoConfig }), [projYears, etfConfig, azioniConfig, obblConfig, depositoConfig]);

  const loadPlan = useCallback((planId) => {
    const plan = investmentPlans.find(p => p.id === planId);
    if (!plan) return;
    setActivePlanId(planId);
    setProjYears(plan.projYears ?? 10);
    setEtfConfig(plan.etfConfig || plan.etf || DEFAULT_CONFIG);
    setAzioniConfig(plan.azioniConfig || plan.azioni || plan.azioneConfig || plan.azione || DEFAULT_CONFIG);
    setObblConfig(plan.obblConfig || plan.obbligazioniConfig || plan.obbConfig || plan.obbligazioni || DEFAULT_CONFIG);
    setDepositoConfig(plan.depositoConfig || plan.depositConfig || plan.deposito || plan.deposit || DEFAULT_CONFIG);
  }, [investmentPlans]);

  const handleSaveNewPlan = useCallback(async () => {
    if (!planNameInput.trim() || !addDocument) return;
    setPlanSaving(true);
    try {
      await addDocument('investmentPlans', { name: planNameInput.trim(), ...currentPlanState() });
    } finally {
      setPlanSaving(false);
      setShowPlanModal(null);
      setPlanNameInput('');
    }
  }, [planNameInput, addDocument, currentPlanState]);

  const handleUpdatePlan = useCallback(async () => {
    if (!activePlanId || !updateDocument) return;
    await updateDocument('investmentPlans', activePlanId, currentPlanState());
  }, [activePlanId, updateDocument, currentPlanState]);

  const handleRenamePlan = useCallback(async () => {
    if (!activePlanId || !planNameInput.trim() || !updateDocument) return;
    setPlanSaving(true);
    try {
      await updateDocument('investmentPlans', activePlanId, { name: planNameInput.trim() });
    } finally {
      setPlanSaving(false);
      setShowPlanModal(null);
      setPlanNameInput('');
    }
  }, [activePlanId, planNameInput, updateDocument]);

  const handleDeletePlan = useCallback(async (id) => {
    if (!deleteDocument) return;
    await deleteDocument('investmentPlans', id);
    if (activePlanId === id) setActivePlanId(null);
    setSelectedComparisonIds(prev => prev.filter(x => x !== id));
  }, [deleteDocument, activePlanId]);

  // Pure projection for a given plan config — returns [{year, total}] from Oggi to +projYears
  const computePlanPoints = useCallback((planCfg) => {
    const cfg_dep = planCfg.depositoConfig || planCfg.depositConfig || planCfg.deposito || planCfg.deposit || DEFAULT_CONFIG;
    const cfg_etf = planCfg.etfConfig || planCfg.etf || DEFAULT_CONFIG;
    const cfg_azi = planCfg.azioniConfig || planCfg.azioni || planCfg.azioneConfig || planCfg.azione || DEFAULT_CONFIG;
    const cfg_obb = planCfg.obblConfig || planCfg.obbligazioniConfig || planCfg.obbConfig || planCfg.obbligazioni || DEFAULT_CONFIG;

    let b_dep = depositValue + (cfg_dep.immediateChange || 0);
    let b_etf = etfValue + (cfg_etf.immediateChange || 0);
    let b_azi = azioniValue + (cfg_azi.immediateChange || 0);
    let b_obb = obbligazioniValue + (cfg_obb.immediateChange || 0);

    const baseYear = new Date().getFullYear();
    const pts = [{ year: 'Oggi', total: Math.round(liquidValue + b_dep + b_etf + b_azi + b_obb) }];

    for (let yr = 1; yr <= projYears; yr++) {
      const add_dep = getPhaseContribution(cfg_dep, yr);
      const add_etf = getPhaseContribution(cfg_etf, yr);
      const add_azi = getPhaseContribution(cfg_azi, yr);
      const add_obb = getPhaseContribution(cfg_obb, yr);
      for (let m = 1; m <= 12; m++) {
        b_dep += b_dep * (avgExpectedDeposit / 100 / 12) + add_dep;
        b_etf += b_etf * (avgExpectedEtf / 100 / 12) + add_etf;
        b_azi += b_azi * (avgExpectedAzioni / 100 / 12) + add_azi;
        b_obb += b_obb * (avgExpectedObbl / 100 / 12) + add_obb;
      }
      pts.push({ year: `${baseYear + yr}`, total: Math.round(liquidValue + b_dep + b_etf + b_azi + b_obb) });
    }
    return pts;
  }, [depositValue, etfValue, azioniValue, obbligazioniValue, liquidValue,
      avgExpectedDeposit, avgExpectedEtf, avgExpectedAzioni, avgExpectedObbl,
      projYears, getPhaseContribution]);

  // Merge all selected plans into a single dataset keyed by year label
  const comparisonChartData = useMemo(() => {
    if (!comparisonMode || selectedComparisonIds.length === 0) return [];

    // Build list of {id, name, cfg} for each selected entry
    const entries = selectedComparisonIds.map(sid => {
      if (sid === 'current') {
        return { id: 'current', name: 'Configurazione attuale', cfg: { etfConfig, azioniConfig, obblConfig: obblConfig, depositoConfig } };
      }
      const plan = investmentPlans.find(p => p.id === sid);
      return plan ? { id: plan.id, name: plan.name, cfg: plan } : null;
    }).filter(Boolean);

    if (entries.length === 0) return [];

    const allPoints = entries.map(e => ({ ...e, pts: computePlanPoints(e.cfg) }));
    const master = allPoints[0].pts;

    return master.map((p, i) => {
      const row = { year: p.year };
      allPoints.forEach(e => { row[e.id] = e.pts[i]?.total ?? null; });
      return row;
    });
  }, [comparisonMode, selectedComparisonIds, investmentPlans, computePlanPoints,
      etfConfig, azioniConfig, obblConfig, depositoConfig]);

  // Toggle a plan in/out of comparison selection
  const toggleComparisonId = useCallback((id) => {
    setSelectedComparisonIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);


  // Future compound projection simulation
  const projectedFuturePoints = useMemo(() => {
    const data = [];
    const r_dep = avgExpectedDeposit;
    const r_etf = avgExpectedEtf;
    const r_azi = avgExpectedAzioni;
    const r_obb = avgExpectedObbl;

    let b_dep = depositValue + (depositoConfig.immediateChange || 0);
    let b_etf = etfValue + (etfConfig.immediateChange || 0);
    let b_azi = azioniValue + (azioniConfig.immediateChange || 0);
    let b_obb = obbligazioniValue + (obblConfig.immediateChange || 0);

    let b_dep_capital = b_dep;
    let b_etf_capital = b_etf;
    let b_azi_capital = b_azi;
    let b_obb_capital = b_obb;

    let accumulatedInterestDep = 0;
    let accumulatedInterestEtf = 0;
    let accumulatedInterestAzi = 0;
    let accumulatedInterestObb = 0;

    // Transition point connects history and future
    const currentCapital = liquidValue + b_dep_capital + b_etf_capital + b_azi_capital + b_obb_capital;
    data.push({
      year: 'Oggi',
      tooltipLabel: 'Oggi (Inizio Proiezione)',
      capitale: Number(currentCapital.toFixed(0)),
      int_dep: 0,
      int_etf: 0,
      int_azioni: 0,
      int_obbl: 0,
      total: Number(currentCapital.toFixed(0)),
      isFuture: true,
    });

    const baseYear = new Date().getFullYear();
    for (let yr = 1; yr <= projYears; yr++) {
      const add_dep = getPhaseContribution(depositoConfig, yr);
      const add_etf = getPhaseContribution(etfConfig, yr);
      const add_azi = getPhaseContribution(azioniConfig, yr);
      const add_obb = getPhaseContribution(obblConfig, yr);

      for (let m = 1; m <= 12; m++) {
        const int_dep = b_dep * (r_dep / 100 / 12);
        const int_etf = b_etf * (r_etf / 100 / 12);
        const int_azi = b_azi * (r_azi / 100 / 12);
        const int_obb = b_obb * (r_obb / 100 / 12);

        accumulatedInterestDep += int_dep;
        accumulatedInterestEtf += int_etf;
        accumulatedInterestAzi += int_azi;
        accumulatedInterestObb += int_obb;

        b_dep += int_dep + add_dep;
        b_etf += int_etf + add_etf;
        b_azi += int_azi + add_azi;
        b_obb += int_obb + add_obb;

        b_dep_capital += add_dep;
        b_etf_capital += add_etf;
        b_azi_capital += add_azi;
        b_obb_capital += add_obb;
      }

      const totalCap = liquidValue + b_dep_capital + b_etf_capital + b_azi_capital + b_obb_capital;
      const totalInt = accumulatedInterestDep + accumulatedInterestEtf + accumulatedInterestAzi + accumulatedInterestObb;
      const futureYear = baseYear + yr;

      data.push({
        year: String(futureYear),
        tooltipLabel: `Anno ${futureYear}`,
        capitale: Number(totalCap.toFixed(0)),
        int_dep: Number(accumulatedInterestDep.toFixed(0)),
        int_etf: Number(accumulatedInterestEtf.toFixed(0)),
        int_azioni: Number(accumulatedInterestAzi.toFixed(0)),
        int_obbl: Number(accumulatedInterestObb.toFixed(0)),
        total: Number((totalCap + totalInt).toFixed(0)),
        isFuture: true,
      });
    }

    return data;
  }, [
    depositValue, etfValue, azioniValue, obbligazioniValue, liquidValue,
    avgExpectedDeposit, avgExpectedEtf, avgExpectedAzioni, avgExpectedObbl,
    projYears, getPhaseContribution,
    depositoConfig, etfConfig, azioniConfig, obblConfig
  ]);

  // Combine past calendar years and future projection years
  const timelineChartData = useMemo(() => {
    return [...historicalPastYears, ...projectedFuturePoints];
  }, [historicalPastYears, projectedFuturePoints]);

  // Million Milestone Flags Detector
  const milestones = useMemo(() => {
    const list = [];
    let highestMillion = 0;
    
    timelineChartData.forEach(point => {
      const currentMillion = Math.floor(point.total / 1000000);
      if (currentMillion > highestMillion) {
        for (let m = highestMillion + 1; m <= currentMillion; m++) {
          list.push({
            year: point.year,
            value: m * 1000000,
            label: `${m}M!`
          });
        }
        highestMillion = currentMillion;
      }
    });
    return list;
  }, [timelineChartData]);




  const monthlySavingsInvestmentTrend = useMemo(() => {
    const trend = [];
    for (let i = 5; i >= 0; i--) {
      const d = subMonths(now, i);
      const start = startOfMonth(d);
      const end = endOfMonth(d);
      const label = format(d, "MMM ''yy", { locale: it });

      let monthIncomes = 0;
      incomes.forEach(tx => {
        if (tx.date) {
          const txDate = new Date(tx.date);
          if (txDate >= start && txDate <= end && tx.category !== 'Trasferimento') {
            monthIncomes += Number(tx.amount) || 0;
          }
        }
      });

      let monthExpenses = 0;
      expenses.forEach(tx => {
        if (tx.date) {
          const txDate = new Date(tx.date);
          if (txDate >= start && txDate <= end && tx.category !== 'Trasferimento') {
            monthExpenses += Number(tx.amount) || 0;
          }
        }
      });

      const savings = monthIncomes - monthExpenses;

      let monthInvestIn = 0;
      incomes.forEach(tx => {
        if (tx.date && tx.category === 'Trasferimento') {
          const txDate = new Date(tx.date);
          if (txDate >= start && txDate <= end) {
            const wallet = walletsAsOfPreviousMonth.find(w => w.id === tx.walletId);
            if (wallet && isSecurityWallet(wallet)) {
              monthInvestIn += Number(tx.amount) || 0;
            }
          }
        }
      });

      let monthInvestOut = 0;
      expenses.forEach(tx => {
        if (tx.date && tx.category === 'Trasferimento') {
          const txDate = new Date(tx.date);
          if (txDate >= start && txDate <= end) {
            const wallet = walletsAsOfPreviousMonth.find(w => w.id === tx.walletId);
            if (wallet && isSecurityWallet(wallet)) {
              monthInvestOut += Number(tx.amount) || 0;
            }
          }
        }
      });

      const investment = monthInvestIn - monthInvestOut;

      trend.push({
        label,
        savings: Number(savings.toFixed(2)),
        investment: Number(investment.toFixed(2))
      });
    }
    return trend;
  }, [incomes, expenses, walletsAsOfPreviousMonth, now]);

  const coverageRatio = recommendedEmergencyFund > 0 ? (emergencyFund / recommendedEmergencyFund) : 0;
  const coveragePctVisual = Math.min(Math.round(coverageRatio * 100), 100);
  const coveragePctText = Math.round(coverageRatio * 100);

  // ── Per-wallet flows ──────────────────────────────────────────────────────
  const walletFlows = useMemo(() => {
    const map = {};
    allInvestmentWallets.forEach(w => { map[w.id] = { in: 0, out: 0 }; });
    incomes.forEach(tx => { if (map[tx.walletId] !== undefined) map[tx.walletId].in += Number(tx.amount) || 0; });
    expenses.forEach(tx => { if (map[tx.walletId] !== undefined) map[tx.walletId].out += Number(tx.amount) || 0; });
    return map;
  }, [allInvestmentWallets, incomes, expenses]);

  const totalFlowIn  = Object.values(walletFlows).reduce((s, f) => s + f.in, 0);
  const totalFlowOut = Object.values(walletFlows).reduce((s, f) => s + f.out, 0);
  const netFlow      = totalFlowIn - totalFlowOut;

  // ── Asset allocation (securities only) ────────────────────────────────────
  const allocationByType = useMemo(() => {
    const map = {};
    securityWallets.forEach(w => {
      const t = classifyWallet(w);
      map[t] = (map[t] || 0) + (w.balance || 0);
    });
    return Object.entries(map).map(([t, v]) => ({
      name: TYPE_LABEL[t], type: t, value: v,
      pct: pct(v, securityValue), color: TYPE_COLOR[t] || '#888',
    })).sort((a, b) => b.value - a.value);
  }, [securityWallets, securityValue]);

  // ── Aggregate monthly trend for all securities ────────────────────────────
  const secMonthlyTrend = useMemo(() => {
    const ids = new Set(securityWallets.map(w => w.id));
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = subMonths(new Date(), i);
      const start = startOfMonth(d); const end = endOfMonth(d);
      const label = format(d, "MMM ''yy", { locale: it });
      const inc = incomes.filter(tx => ids.has(tx.walletId) && tx.date && new Date(tx.date) >= start && new Date(tx.date) <= end).reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
      const exp = expenses.filter(tx => ids.has(tx.walletId) && tx.date && new Date(tx.date) >= start && new Date(tx.date) <= end).reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
      months.push({ label, net: inc - exp });
    }
    let cum = 0;
    return months.map(m => { cum += m.net; return { ...m, capital: cum - m.net, total: cum }; });
  }, [securityWallets, incomes, expenses]);

  // ── Deposit interest totals ───────────────────────────────────────────────
  const depositProj = useMemo(() =>
    depositWallets.map(w => {
      const p = w.balance || 0;
      const r = parseFloat(w.grossAnnualRate) || 0;
      const gross = p * r / 100;
      return { wallet: w, principal: p, rate: r, gross, net: gross * 0.74, netMonthly: gross * 0.74 / 12 };
    }), [depositWallets]);
  const totalGross = depositProj.reduce((s, d) => s + d.gross, 0);
  const totalNet   = depositProj.reduce((s, d) => s + d.net, 0);

  // ── Section nav ──────────────────────────────────────────────────────────
  const sections = [
    { id: 'overview',      label: 'Panoramica',    icon: Activity },
    ...(etfWallets.length > 0          ? [{ id: 'etf',          label: 'ETF',           icon: BarChart2  }] : []),
    ...(azioniWallets.length > 0       ? [{ id: 'azioni',       label: 'Azioni',        icon: TrendingUp }] : []),
    ...(obbligazioniWallets.length > 0 ? [{ id: 'obbligazioni', label: 'Obbligazioni',  icon: Layers     }] : []),
    ...(depositWallets.length > 0      ? [{ id: 'deposito',     label: 'Deposito',      icon: Shield     }] : []),
    { id: 'ai',            label: 'Consulente AI', icon: Brain },
  ];

  return (
    <div className="space-y-5 pb-32">
      {/* Section nav */}
      <div className="overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max pb-1">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
                activeSection === s.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-white/60 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-400'
              }`}>
              <s.icon className="w-3.5 h-3.5" />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════ PANORAMICA ═══════════════════ */}
      {activeSection === 'overview' && (
        allInvestmentWallets.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-20 px-6 space-y-4 animate-fade-in-up">
            <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-3xl flex items-center justify-center shadow-md">
              <TrendingUp className="w-10 h-10" />
            </div>
            <h3 className="text-xl font-black text-gray-900 dark:text-gray-100">Nessun conto investimento trovato</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed">
              Aggiungi un conto di tipo <strong>ETF</strong>, <strong>Azioni</strong>, <strong>Obbligazioni</strong> o <strong>Conto Deposito</strong> dalla sezione <em>Conti</em> (saldo &gt; 0) per visualizzare la dashboard.
            </p>
            <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 text-blue-600 text-xs font-bold px-4 py-2 rounded-full">
              <AlertCircle className="w-3.5 h-3.5" /> Conti con saldo zero vengono ignorati
            </div>
          </div>
        ) : (
          <div className="space-y-4 animate-fade-in-up">
          {/* Grid delle 4 schede di ripartizione (2 per riga - Ultra Leggere e Colorate) */}
          <div className="grid grid-cols-2 gap-2.5">
            {/* Liquidità */}
            <div className="card p-3 bg-gradient-to-br from-sky-500/10 to-sky-600/5 border border-sky-500/20 dark:from-sky-500/20 dark:to-sky-600/5 dark:border-sky-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
              <div className="absolute top-0 right-0 w-16 h-16 bg-sky-500/5 rounded-full -mr-4 -mt-4 pointer-events-none" />
              <div className="flex items-center gap-1">
                <Wallet className="w-3 h-3 text-sky-500" />
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-sky-600 dark:text-sky-300">Liquidità</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-base font-black text-gray-950 dark:text-sky-100">{fmt(liquidValue)}</span>
                <span className="text-xs font-bold text-sky-500 dark:text-sky-400">{liquidPct}%</span>
              </div>
            </div>

            {/* Deposito */}
            <div className="card p-3 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 dark:from-emerald-500/20 dark:to-emerald-600/5 dark:border-emerald-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
              <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 rounded-full -mr-4 -mt-4 pointer-events-none" />
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-emerald-500" />
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-emerald-600 dark:text-emerald-300">Deposito</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-base font-black text-gray-950 dark:text-emerald-100">{fmt(depositValue)}</span>
                <span className="text-xs font-bold text-emerald-500 dark:text-emerald-400">{depositPct}%</span>
              </div>
            </div>

            {/* Titoli */}
            <div className="card p-3 bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/20 dark:from-violet-500/20 dark:to-violet-600/5 dark:border-violet-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
              <div className="absolute top-0 right-0 w-16 h-16 bg-violet-500/5 rounded-full -mr-4 -mt-4 pointer-events-none" />
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-violet-500" />
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-violet-600 dark:text-violet-300">Titoli</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-base font-black text-gray-950 dark:text-violet-100">{fmt(securityValue)}</span>
                <span className="text-xs font-bold text-violet-500 dark:text-violet-400">{securityPct}%</span>
              </div>
            </div>

            {/* Allocazione */}
            <div className="card p-3 bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 dark:from-amber-500/20 dark:to-amber-600/5 dark:border-amber-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/5 rounded-full -mr-4 -mt-4 pointer-events-none" />
              <div className="flex items-center gap-1">
                <Layers className="w-3 h-3 text-amber-500" />
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-amber-600 dark:text-amber-300">Allocazione</span>
              </div>
              <div className="mt-2">
                <div className="w-full h-3 bg-gray-100 dark:bg-gray-750 rounded-full overflow-hidden flex shadow-inner">
                  {liquidValue > 0 && <div className="bg-sky-500 h-full" style={{ width: `${liquidPctVal}%` }} />}
                  {depositValue > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${depositPctVal}%` }} />}
                  {securityValue > 0 && <div className="bg-violet-500 h-full" style={{ width: `${securityPctVal}%` }} />}
                </div>
              </div>
            </div>
          </div>

          {/* Stacked AreaChart */}
          <div className="card p-4 space-y-4">
            <div className="flex items-center justify-between gap-3 w-full">
              <div className="-mb-4">
                <SectionTitle icon={Activity} title="Andamento Patrimonio" color="#6366f1" />
              </div>
              
              {/* Timeframe Selector Button Group next to title */}
              <div className="flex bg-gray-100 dark:bg-gray-800/80 p-0.5 rounded-lg border border-gray-200/50 dark:border-gray-700/50">
                {['6M', '12M', 'MAX'].map(tf => (
                  <button
                    key={tf}
                    type="button"
                    onClick={() => setOverviewTimeframe(tf)}
                    className={`px-2.5 py-0.5 rounded-md text-[10px] font-extrabold transition-all ${
                      overviewTimeframe === tf
                        ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {tf}
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{ height: 260 }} className="w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stackedWeeklyTrend} margin={{ top: 10, right: 5, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorLiquidita" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorDeposito" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorTitoli" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorInteressi" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.15} />
                  <XAxis
                    dataKey="label"
                    tickFormatter={(tick) => tick.includes('_w') ? '' : tick}
                    tick={{ fontSize: 9, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={44} />
                  <Tooltip content={<StackedTooltip />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 10, paddingTop: 10 }} />
                  <Area type="monotone" dataKey="liquidita" name="Liquidità" stackId="1" stroke="#0ea5e9" fill="url(#colorLiquidita)" strokeWidth={2} />
                  <Area type="monotone" dataKey="deposito" name="Deposito" stackId="1" stroke="#10b981" fill="url(#colorDeposito)" strokeWidth={2} />
                  <Area type="monotone" dataKey="titoli" name="Titoli" stackId="1" stroke="#8b5cf6" fill="url(#colorTitoli)" strokeWidth={2} />
                  <Area type="monotone" dataKey="interessi" name="Rendimento" stackId="1" stroke="#eab308" fill="url(#colorInteressi)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Nuove Schede: Medie di Risparmio ed Investimento (2 per riga - Ultra Leggere e Colorate) */}
          <div className="grid grid-cols-2 gap-2.5">
            {/* Media Risparmio Mensile */}
            <div className="card p-3 bg-gradient-to-br from-sky-500/5 to-transparent border border-sky-500/10 dark:from-sky-500/10 dark:to-transparent dark:border-sky-500/20 rounded-xl flex flex-col justify-between relative overflow-hidden shadow-none">
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-sky-500" />
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-sky-600 dark:text-sky-300">Risparmio/Mese</span>
              </div>
              <div className="mt-2">
                <span className="text-base font-black text-gray-950 dark:text-sky-100">{fmt(avgSavings6M)}</span>
              </div>
            </div>

            {/* Media Investimento Mensile */}
            <div className="card p-3 bg-gradient-to-br from-violet-500/5 to-transparent border border-violet-500/10 dark:from-violet-500/10 dark:to-transparent dark:border-violet-500/20 rounded-xl flex flex-col justify-between relative overflow-hidden shadow-none">
              <div className="flex items-center gap-1">
                <PieChartIcon className="w-3 h-3 text-violet-500" />
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-violet-600 dark:text-violet-350">Investito/Mese</span>
              </div>
              <div className="mt-2">
                <span className="text-base font-black text-gray-950 dark:text-violet-100">{fmt(avgInvestment6M)}</span>
              </div>
            </div>
          </div>

          {/* Target Asset Allocation: PAC, Fondo Emergenza, ETF, Azioni, Obbligazioni */}
          <div className="space-y-2">
            <div className="text-[9px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 px-1 mt-2">Target Asset Allocation</div>

            {/* PAC Row */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="card p-2 bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/20 dark:from-violet-500/20 dark:to-violet-600/5 dark:border-violet-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-violet-800 dark:text-violet-300">
                  <PieChartIcon className="w-3 h-3 text-violet-500" />
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-violet-600 dark:text-violet-300">PAC Attuale</span>
                </div>
                <div className="mt-1.5">
                  <span className="text-xs font-black text-gray-950 dark:text-violet-100">{fmt(pacAttuale)}</span>
                </div>
              </div>
              {userSettings?.aiInvestmentPlan?.recommendedPac ? (
                <div className="card p-2 bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/20 dark:from-violet-500/20 dark:to-violet-600/5 dark:border-violet-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                  <div className="flex items-center gap-1 justify-between">
                    <div className="flex items-center gap-1">
                      <PieChartIcon className="w-3 h-3 text-violet-500" />
                      <span className="text-[9px] uppercase tracking-wider font-extrabold text-violet-600 dark:text-violet-300">PAC Consigliato</span>
                    </div>
                    <span className="text-[8px] px-1 py-0.5 rounded font-black bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400">AI</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-xs font-black text-violet-700 dark:text-violet-200">{fmt(userSettings.aiInvestmentPlan.recommendedPac)}</span>
                  </div>
                </div>
              ) : (
                <div className="card p-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-855 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none opacity-50">
                  <div className="flex items-center gap-1 text-gray-400">
                    <PieChartIcon className="w-3 h-3 text-gray-400" />
                    <span className="text-[9px] uppercase tracking-wider font-extrabold text-gray-455">PAC Consigliato</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-[9px] font-black text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 rounded">Da definire</span>
                  </div>
                </div>
              )}
            </div>

            {/* Fondo Emergenza Row */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="card p-2 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 dark:from-emerald-500/20 dark:to-emerald-600/5 dark:border-emerald-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-emerald-800 dark:text-emerald-300">
                  <Shield className="w-3 h-3 text-emerald-500" />
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-emerald-600 dark:text-emerald-300">Fondo Emergenza</span>
                </div>
                <div className="mt-1.5">
                  <span className="text-xs font-black text-gray-950 dark:text-emerald-100">{fmt(emergencyFund)}</span>
                </div>
              </div>
              {userSettings?.aiInvestmentPlan?.recommendedEmergencyFund ? (
                <div className="card p-2 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 dark:from-emerald-500/20 dark:to-emerald-600/5 dark:border-emerald-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                  <div className="flex items-center gap-1 justify-between">
                    <div className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-emerald-500" />
                      <span className="text-[9px] uppercase tracking-wider font-extrabold text-emerald-600 dark:text-emerald-300">F. Emergenza Cons.</span>
                    </div>
                    <span className="text-[8px] px-1 py-0.5 rounded font-black bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">AI</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-xs font-black text-emerald-700 dark:text-emerald-200">{fmt(userSettings.aiInvestmentPlan.recommendedEmergencyFund)}</span>
                  </div>
                </div>
              ) : (
                <div className="card p-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-855 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none opacity-50">
                  <div className="flex items-center gap-1 text-gray-400">
                    <Shield className="w-3 h-3 text-gray-400" />
                    <span className="text-[9px] uppercase tracking-wider font-extrabold text-gray-455">F. Emergenza Cons.</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-[9px] font-black text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 rounded">Da definire</span>
                  </div>
                </div>
              )}
            </div>


            {/* ETF Row */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="card p-2 bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20 dark:from-yellow-500/20 dark:to-yellow-600/5 dark:border-yellow-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-yellow-800 dark:text-yellow-300">
                  <span className="text-xs">🧺</span>
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-yellow-600 dark:text-yellow-300">Totale ETF</span>
                </div>
                <div className="mt-1.5">
                  <span className="text-xs font-black text-gray-950 dark:text-yellow-100">{fmt(etfValue)}</span>
                </div>
              </div>
              {userSettings?.aiInvestmentPlan?.recommendedAllocation?.etf !== undefined ? (
                <div className="card p-2 bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20 dark:from-yellow-500/20 dark:to-yellow-600/5 dark:border-yellow-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                  <div className="flex items-center gap-1 justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-xs">🧺</span>
                      <span className="text-[9px] uppercase tracking-wider font-extrabold text-yellow-600 dark:text-yellow-300">ETF Consigliato</span>
                    </div>
                    <span className="text-[8px] px-1 py-0.5 rounded font-black bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400">AI</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-xs font-black text-yellow-700 dark:text-yellow-200">
                      {fmt((userSettings.aiInvestmentPlan.recommendedAllocation.etf / 100) * totalInvestedPortfolio)} ({userSettings.aiInvestmentPlan.recommendedAllocation.etf}%)
                    </span>
                  </div>
                </div>
              ) : (
                <div className="card p-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-855 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none opacity-50">
                  <div className="flex items-center gap-1 text-gray-400">
                    <span className="text-xs">🧺</span>
                    <span className="text-[9px] uppercase tracking-wider font-extrabold text-gray-455">Consigliato ETF</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-[9px] font-black text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 rounded">Da definire</span>
                  </div>
                </div>
              )}
            </div>

            {/* Azioni Row */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="card p-2 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 dark:from-indigo-500/20 dark:to-indigo-600/5 dark:border-indigo-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-indigo-800 dark:text-indigo-300">
                  <span className="text-xs">📈</span>
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-indigo-600 dark:text-indigo-300">Totale Azioni</span>
                </div>
                <div className="mt-1.5">
                  <span className="text-xs font-black text-gray-950 dark:text-indigo-100">{fmt(azioniValue)}</span>
                </div>
              </div>
              {userSettings?.aiInvestmentPlan?.recommendedAllocation?.azioni !== undefined ? (
                <div className="card p-2 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 dark:from-indigo-500/20 dark:to-indigo-600/5 dark:border-indigo-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                  <div className="flex items-center gap-1 justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-xs">📈</span>
                      <span className="text-[9px] uppercase tracking-wider font-extrabold text-indigo-600 dark:text-indigo-300">Azioni Cons.</span>
                    </div>
                    <span className="text-[8px] px-1 py-0.5 rounded font-black bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">AI</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-xs font-black text-indigo-700 dark:text-indigo-200">
                      {fmt((userSettings.aiInvestmentPlan.recommendedAllocation.azioni / 100) * totalInvestedPortfolio)} ({userSettings.aiInvestmentPlan.recommendedAllocation.azioni}%)
                    </span>
                  </div>
                </div>
              ) : (
                <div className="card p-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-855 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none opacity-50">
                  <div className="flex items-center gap-1 text-gray-400">
                    <span className="text-xs">📈</span>
                    <span className="text-[9px] uppercase tracking-wider font-extrabold text-gray-455">Consigliato Azioni</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-[9px] font-black text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 rounded">Da definire</span>
                  </div>
                </div>
              )}
            </div>

            {/* Obbligazioni Row */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="card p-2 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 dark:from-orange-500/20 dark:to-orange-600/5 dark:border-orange-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-orange-800 dark:text-orange-300">
                  <span className="text-xs">📜</span>
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-orange-600 dark:text-orange-300">Totale Obbligazioni</span>
                </div>
                <div className="mt-1.5">
                  <span className="text-xs font-black text-gray-950 dark:text-orange-100">{fmt(obbligazioniValue)}</span>
                </div>
              </div>
              {userSettings?.aiInvestmentPlan?.recommendedAllocation?.obbligazioni !== undefined ? (
                <div className="card p-2 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 dark:from-orange-500/20 dark:to-orange-600/5 dark:border-orange-500/30 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none">
                  <div className="flex items-center gap-1 justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-xs">📜</span>
                      <span className="text-[9px] uppercase tracking-wider font-extrabold text-orange-600 dark:text-orange-300">Obbl. Cons.</span>
                    </div>
                    <span className="text-[8px] px-1 py-0.5 rounded font-black bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400">AI</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-xs font-black text-orange-700 dark:text-orange-200">
                      {fmt((userSettings.aiInvestmentPlan.recommendedAllocation.obbligazioni / 100) * totalInvestedPortfolio)} ({userSettings.aiInvestmentPlan.recommendedAllocation.obbligazioni}%)
                    </span>
                  </div>
                </div>
              ) : (
                <div className="card p-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-855 rounded-xl relative overflow-hidden flex flex-col justify-between shadow-none opacity-50">
                  <div className="flex items-center gap-1 text-gray-400">
                    <span className="text-xs">📜</span>
                    <span className="text-[9px] uppercase tracking-wider font-extrabold text-gray-455">Consigliato Obbl.</span>
                  </div>
                  <div className="mt-1.5">
                    <span className="text-[9px] font-black text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 rounded">Da definire</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Nuovo Grafico: Savings vs Investment Trend */}
          {monthlySavingsInvestmentTrend.length > 0 && (
            <div className="card p-4 space-y-4">
              <div className="-mb-4">
                <SectionTitle icon={BarChart2} title="Rapporto Risparmio e Investimento" color="#3b82f6" />
              </div>
              <div style={{ height: 240 }} className="w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlySavingsInvestmentTrend} margin={{ top: 10, right: 5, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.15} />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={44} />
                    <Tooltip content={<SavingsInvestmentTooltip />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 10, paddingTop: 10 }} />
                    <Bar dataKey="savings" name="Risparmio" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={16} />
                    <Bar dataKey="investment" name="Investimento Netto" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Rendimenti Medi Attesi & Effettivi */}
          <div className="space-y-2.5">
            <div className="text-[9px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 px-1 mt-2">Rendimenti Annualizzati</div>
            
            {/* Grid 4 tipologie */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              {/* ETF */}
              <div className="card p-2 bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20 dark:from-yellow-500/20 dark:to-yellow-600/5 dark:border-yellow-500/30 rounded-xl flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-yellow-800 dark:text-yellow-300">
                  <span className="text-xs">🧺</span>
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-yellow-600 dark:text-yellow-300">Exp. ETF</span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-0.5">
                  <span className="text-xs font-black text-gray-950 dark:text-yellow-100">{avgExpectedEtf.toFixed(2)}%</span>
                  <span className="text-[8px] text-gray-400 font-bold">atteso</span>
                </div>
              </div>

              {/* Azioni */}
              <div className="card p-2 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 dark:from-indigo-500/20 dark:to-indigo-600/5 dark:border-indigo-500/30 rounded-xl flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-indigo-800 dark:text-indigo-300">
                  <span className="text-xs">📈</span>
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-indigo-600 dark:text-indigo-300">Exp. Azioni</span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-0.5">
                  <span className="text-xs font-black text-gray-950 dark:text-indigo-100">{avgExpectedAzioni.toFixed(2)}%</span>
                  <span className="text-[8px] text-gray-400 font-bold">atteso</span>
                </div>
              </div>

              {/* Obbligazioni */}
              <div className="card p-2 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 dark:from-orange-500/20 dark:to-orange-600/5 dark:border-orange-500/30 rounded-xl flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-orange-800 dark:text-orange-300">
                  <span className="text-xs">📜</span>
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-orange-600 dark:text-orange-300">Exp. Obbligazioni</span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-0.5">
                  <span className="text-xs font-black text-gray-950 dark:text-orange-100">{avgExpectedObbl.toFixed(2)}%</span>
                  <span className="text-[8px] text-gray-400 font-bold">atteso</span>
                </div>
              </div>

              {/* Deposito */}
              <div className="card p-2 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 dark:from-emerald-500/20 dark:to-emerald-600/5 dark:border-emerald-500/30 rounded-xl flex flex-col justify-between shadow-none">
                <div className="flex items-center gap-1 text-emerald-800 dark:text-emerald-300">
                  <span className="text-xs">🏦</span>
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-emerald-600 dark:text-emerald-300">Exp. Deposito</span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-0.5">
                  <span className="text-xs font-black text-gray-950 dark:text-emerald-100">
                    {avgExpectedDeposit > 0 ? `${avgExpectedDeposit.toFixed(2)}%` : '0.00%'}
                  </span>
                  <span className="text-[8px] text-gray-400 font-bold">pesato</span>
                </div>
              </div>
            </div>

            {/* Grid 2 totali */}
            <div className="grid grid-cols-2 gap-2.5">
              {/* Rendimento Atteso Totale */}
              <div className="card p-2.5 bg-gradient-to-br from-sky-500/10 to-sky-600/5 border border-sky-500/20 dark:from-sky-500/20 dark:to-sky-600/5 dark:border-sky-500/30 rounded-xl flex flex-col justify-between relative overflow-hidden shadow-none">
                <div className="absolute top-0 right-0 w-16 h-16 bg-sky-500/5 rounded-full -mr-4 -mt-4 pointer-events-none" />
                <div className="flex items-center gap-1 text-sky-800 dark:text-sky-300">
                  <Percent className="w-3.5 h-3.5 text-sky-500" />
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-sky-600 dark:text-sky-300">Rendimento Atteso Totale</span>
                </div>
                <div className="mt-2">
                  <span className="text-base font-black text-gray-950 dark:text-sky-100">
                    {avgExpectedTotal > 0 ? `${avgExpectedTotal.toFixed(2)}%` : '0.00%'}
                  </span>
                </div>
              </div>

              {/* Rendimento Attuale Effettivo */}
              <div className="card p-2.5 bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/20 dark:from-violet-500/20 dark:to-violet-600/5 dark:border-violet-500/30 rounded-xl flex flex-col justify-between relative overflow-hidden shadow-none">
                <div className="absolute top-0 right-0 w-16 h-16 bg-violet-500/5 rounded-full -mr-4 -mt-4 pointer-events-none" />
                <div className="flex items-center gap-1 text-violet-800 dark:text-violet-300">
                  <Zap className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-violet-600 dark:text-violet-350">Rendimento Effettivo</span>
                </div>
                <div className="mt-2">
                  <span className="text-base font-black text-gray-950 dark:text-violet-100">
                    {avgEffectiveActual > 0 ? `${avgEffectiveActual.toFixed(2)}%` : '0.00%'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Proiezione Patrimoniale Futura */}
          <div className="card p-4 space-y-4">
            <div className="flex items-center justify-between gap-3 w-full flex-wrap">
              <div className="-mb-4">
                <SectionTitle icon={Zap} title="Simulazione Timeline Patrimoniale" color="#f59e0b" />
              </div>

              {/* Year count selector */}
              <div className="flex bg-gray-100 dark:bg-gray-800/80 p-0.5 rounded-lg border border-gray-200/50 dark:border-gray-700/50 flex-wrap gap-0.5">
                {[5, 10, 15, 20, 25, 30, 40].map(yr => (
                  <button
                    key={yr}
                    type="button"
                    onClick={() => setProjYears(yr)}
                    className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold transition-all ${
                      projYears === yr
                        ? 'bg-white dark:bg-gray-700 text-yellow-600 dark:text-yellow-450 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {yr}a
                  </button>
                ))}
              </div>
            </div>
            
            {/* Timeline AreaChart (Past MAX + Future ProjYears) */}
            <div style={{ height: 280 }} className="w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timelineChartData} margin={{ top: 15, right: 5, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorCapitale" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#64748b" stopOpacity={0.25}/>
                      <stop offset="95%" stopColor="#64748b" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorIntDep" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorIntEtf" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorIntAzioni" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorIntObbl" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.15} />
                  <XAxis 
                    dataKey="year" 
                    tick={{ fontSize: 9, fill: '#94a3b8' }} 
                    axisLine={false} 
                    tickLine={false} 
                  />
                  <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={44} />
                  
                  {/* Million Milestone markers lines */}
                  {milestones.map((m, idx) => (
                    <ReferenceLine
                      key={idx}
                      x={m.year}
                      stroke="#ef4444"
                      strokeDasharray="3 3"
                      label={{
                        value: m.label,
                        fill: '#ef4444',
                        fontSize: 9,
                        fontWeight: '900',
                        position: 'top'
                      }}
                    />
                  ))}

                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-3 text-xs space-y-1.5 min-w-[175px]">
                        <p className="text-[10px] font-black text-gray-400 uppercase leading-none">{d?.tooltipLabel || d?.year}</p>
                        <div className="space-y-1">
                          <div className="flex justify-between gap-4 text-gray-500 font-bold">
                            <span>Capitale:</span>
                            <span>{fmt(d?.capitale)}</span>
                          </div>
                          {d?.int_dep > 0 && (
                            <div className="flex justify-between gap-4 text-emerald-600 dark:text-emerald-400">
                              <span>Int. Deposito:</span>
                              <span className="font-black">+{fmt(d?.int_dep)}</span>
                            </div>
                          )}
                          {d?.int_etf > 0 && (
                            <div className="flex justify-between gap-4 text-yellow-600 dark:text-yellow-400">
                              <span>Gain ETF:</span>
                              <span className="font-black">+{fmt(d?.int_etf)}</span>
                            </div>
                          )}
                          {d?.int_azioni > 0 && (
                            <div className="flex justify-between gap-4 text-indigo-600 dark:text-indigo-400">
                              <span>Gain Azioni:</span>
                              <span className="font-black">+{fmt(d?.int_azioni)}</span>
                            </div>
                          )}
                          {d?.int_obbl > 0 && (
                            <div className="flex justify-between gap-4 text-orange-600 dark:text-orange-400">
                              <span>Gain Obbligazioni:</span>
                              <span className="font-black">+{fmt(d?.int_obbl)}</span>
                            </div>
                          )}
                          <div className="border-t border-gray-100 dark:border-gray-750 pt-1 flex justify-between gap-4 font-black text-gray-900 dark:text-gray-100">
                            <span>Totale:</span>
                            <span>{fmt(d?.total)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }} />
                  
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 10, paddingTop: 10 }} />
                  <Area type="monotone" dataKey="capitale" name="Capitale Investito" stackId="1" stroke="#64748b" fill="url(#colorCapitale)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="int_dep" name="Interessi Deposito" stackId="1" stroke="#10b981" fill="url(#colorIntDep)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="int_etf" name="Interessi ETF" stackId="1" stroke="#eab308" fill="url(#colorIntEtf)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="int_azioni" name="Interessi Azioni" stackId="1" stroke="#6366f1" fill="url(#colorIntAzioni)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="int_obbl" name="Interessi Obbligazioni" stackId="1" stroke="#f97316" fill="url(#colorIntObbl)" strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Pianificazione rate mensili — con selettore piano in cima */}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800/60 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase font-black tracking-wider text-gray-450 dark:text-gray-500">
                  Pianificazione rate mensili
                </div>
                <button
                  type="button"
                  onClick={() => { setPlanNameInput(''); setShowPlanModal('save'); }}
                  className="text-[10px] font-bold text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0"
                >
                  + Nuovo piano
                </button>
              </div>

              {/* Selettore piano */}
              <div className="flex gap-2 items-center">
                <select
                  value={activePlanId || ''}
                  onChange={(e) => { if (e.target.value) loadPlan(e.target.value); else setActivePlanId(null); }}
                  className="flex-1 min-w-0 text-xs font-semibold bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">— Seleziona piano —</option>
                  {investmentPlans.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {activePlanId && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button type="button" onClick={handleUpdatePlan} title="Aggiorna piano"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors">
                      <Save className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => { setPlanNameInput(investmentPlans.find(p => p.id === activePlanId)?.name || ''); setShowPlanModal('rename'); }} title="Rinomina piano"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => handleDeletePlan(activePlanId)} title="Elimina piano"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* 4 bottoni rate */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 pt-1">
                <button type="button" onClick={() => openConfigModal('etf')}
                  className="card p-2 bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20 dark:from-yellow-500/20 dark:to-yellow-600/5 dark:border-yellow-500/30 rounded-xl flex items-center justify-between text-left hover:brightness-105 active:scale-[0.98] transition-all shadow-none">
                  <div className="min-w-0">
                    <div className="text-[8px] uppercase tracking-wider font-extrabold text-yellow-600 dark:text-yellow-400 flex items-center gap-1"><span>🧺</span> ETF</div>
                    <div className="text-[10px] font-black text-gray-950 dark:text-yellow-100 truncate mt-0.5">
                      {etfConfig.immediateChange !== 0 ? `${etfConfig.immediateChange > 0 ? '+' : ''}${etfConfig.immediateChange} oggi ` : ''}{`Rata: €${etfConfig.phases[0]?.amount || 0}`}
                    </div>
                  </div>
                  <ChevronDown className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                </button>

                <button type="button" onClick={() => openConfigModal('azioni')}
                  className="card p-2 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 dark:from-indigo-500/20 dark:to-indigo-600/5 dark:border-indigo-500/30 rounded-xl flex items-center justify-between text-left hover:brightness-105 active:scale-[0.98] transition-all shadow-none">
                  <div className="min-w-0">
                    <div className="text-[8px] uppercase tracking-wider font-extrabold text-indigo-600 dark:text-indigo-400 flex items-center gap-1"><span>📈</span> Azioni</div>
                    <div className="text-[10px] font-black text-gray-950 dark:text-indigo-100 truncate mt-0.5">
                      {azioniConfig.immediateChange !== 0 ? `${azioniConfig.immediateChange > 0 ? '+' : ''}${azioniConfig.immediateChange} oggi ` : ''}{`Rata: €${azioniConfig.phases[0]?.amount || 0}`}
                    </div>
                  </div>
                  <ChevronDown className="w-3 h-3 text-indigo-500 flex-shrink-0" />
                </button>

                <button type="button" onClick={() => openConfigModal('obbligazioni')}
                  className="card p-2 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 dark:from-orange-500/20 dark:to-orange-600/5 dark:border-orange-500/30 rounded-xl flex items-center justify-between text-left hover:brightness-105 active:scale-[0.98] transition-all shadow-none">
                  <div className="min-w-0">
                    <div className="text-[8px] uppercase tracking-wider font-extrabold text-orange-600 dark:text-orange-400 flex items-center gap-1"><span>📜</span> Obbligazioni</div>
                    <div className="text-[10px] font-black text-gray-950 dark:text-orange-100 truncate mt-0.5">
                      {obblConfig.immediateChange !== 0 ? `${obblConfig.immediateChange > 0 ? '+' : ''}${obblConfig.immediateChange} oggi ` : ''}{`Rata: €${obblConfig.phases[0]?.amount || 0}`}
                    </div>
                  </div>
                  <ChevronDown className="w-3 h-3 text-orange-500 flex-shrink-0" />
                </button>

                <button type="button" onClick={() => openConfigModal('deposito')}
                  className="card p-2 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 dark:from-emerald-500/20 dark:to-emerald-600/5 dark:border-emerald-500/30 rounded-xl flex items-center justify-between text-left hover:brightness-105 active:scale-[0.98] transition-all shadow-none">
                  <div className="min-w-0">
                    <div className="text-[8px] uppercase tracking-wider font-extrabold text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><span>🏦</span> Deposito</div>
                    <div className="text-[10px] font-black text-gray-950 dark:text-emerald-100 truncate mt-0.5">
                      {depositoConfig.immediateChange !== 0 ? `${depositoConfig.immediateChange > 0 ? '+' : ''}${depositoConfig.immediateChange} oggi ` : ''}{`Rata: €${depositoConfig.phases[0]?.amount || 0}`}
                    </div>
                  </div>
                  <ChevronDown className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                </button>
              </div>

              {/* Bottone Confronta in fondo */}
              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => setComparisonMode(m => !m)}
                  className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-extrabold border transition-all ${
                    comparisonMode
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-gray-50 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <BarChart2 className="w-3.5 h-3.5" />
                  {comparisonMode ? 'Chiudi confronto' : 'Confronta piani'}
                </button>
              </div>

              {/* Sezione confronto — toggle */}
              {comparisonMode && (
                <div className="pt-2 border-t border-gray-100 dark:border-gray-800/60 space-y-3 animate-fade-in-up">
                  <div className="text-[10px] uppercase font-black tracking-wider text-gray-500 dark:text-gray-400">
                    Seleziona piani da confrontare
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => toggleComparisonId('current')}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${
                        selectedComparisonIds.includes('current')
                          ? 'text-white border-transparent shadow-sm'
                          : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-400'
                      }`}
                      style={selectedComparisonIds.includes('current') ? { background: COMPARISON_COLORS[0] } : {}}
                    >
                      {selectedComparisonIds.includes('current') && <span className="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />}
                      Configurazione attuale
                    </button>

                    {investmentPlans.map((plan, idx) => {
                      const color = COMPARISON_COLORS[(idx + 1) % COMPARISON_COLORS.length];
                      const selected = selectedComparisonIds.includes(plan.id);
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => toggleComparisonId(plan.id)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${
                            selected
                              ? 'text-white border-transparent shadow-sm'
                              : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-400'
                          }`}
                          style={selected ? { background: color } : {}}
                        >
                          {selected && <span className="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />}
                          {plan.name}
                        </button>
                      );
                    })}
                  </div>

                  {investmentPlans.length === 0 && (
                    <p className="text-[10px] text-gray-400">Nessun piano salvato. Salva prima un piano per confrontarlo.</p>
                  )}

                  {selectedComparisonIds.length === 0 && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center py-3">Seleziona almeno un piano per visualizzare il confronto.</p>
                  )}

                  {comparisonChartData.length > 0 && selectedComparisonIds.length >= 1 && (
                    <div style={{ height: 240 }} className="w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={comparisonChartData} margin={{ top: 10, right: 10, left: 8, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.15} />
                          <XAxis dataKey="year" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={44} />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-3 text-xs space-y-1.5 min-w-[160px]">
                                  <p className="text-[10px] font-black text-gray-400 uppercase">{label}</p>
                                  {payload.map((p) => (
                                    <div key={p.dataKey} className="flex items-center justify-between gap-4">
                                      <div className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
                                        <span className="font-semibold text-gray-700 dark:text-gray-300 truncate max-w-[90px]">{p.name}:</span>
                                      </div>
                                      <span className="font-black text-gray-900 dark:text-gray-100">{fmtShort(p.value)}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          />
                          {selectedComparisonIds.map((sid) => {
                            const color = sid === 'current'
                              ? COMPARISON_COLORS[0]
                              : COMPARISON_COLORS[(investmentPlans.findIndex(p => p.id === sid) + 1) % COMPARISON_COLORS.length];
                            const name = sid === 'current'
                              ? 'Configurazione attuale'
                              : (investmentPlans.find(p => p.id === sid)?.name ?? sid);
                            return (
                              <Line key={sid} type="monotone" dataKey={sid} name={name}
                                stroke={color} strokeWidth={2.5} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                            );
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Plan name modal (save / rename) */}
          {showPlanModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-xs shadow-2xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
                <h3 className="text-sm font-black text-gray-900 dark:text-gray-100">
                  {showPlanModal === 'rename' ? 'Rinomina piano' : 'Salva piano'}
                </h3>
                <input
                  type="text"
                  placeholder="Nome del piano (es: Piano 2030)"
                  value={planNameInput}
                  onChange={(e) => setPlanNameInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { showPlanModal === 'rename' ? handleRenamePlan() : handleSaveNewPlan(); } }}
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-400"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowPlanModal(null); setPlanNameInput(''); }}
                    className="flex-1 py-2 rounded-xl text-xs font-bold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    disabled={planSaving || !planNameInput.trim()}
                    onClick={() => showPlanModal === 'rename' ? handleRenamePlan() : handleSaveNewPlan()}
                    className="flex-1 py-2 rounded-xl text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {planSaving ? 'Salvo...' : 'Salva'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Interactive Multi-Phase Savings Configuration Modal */}
          {activeModalConfig && tempConfig && (() => {
            const modalColor = TYPE_COLOR[activeModalConfig] || '#6366f1';
            const modalLabel = TYPE_LABEL[activeModalConfig] || activeModalConfig;
            const modalIcon = TYPE_ICON[activeModalConfig] || '💼';
            return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
              <div className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-sm shadow-2xl flex flex-col max-h-[88vh] overflow-hidden"
                style={{ border: `1.5px solid ${modalColor}30` }}>
                
                {/* Header colorato */}
                <div className="px-5 pt-5 pb-4" style={{ background: `linear-gradient(135deg, ${modalColor}12, ${modalColor}06)` }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                        style={{ background: `${modalColor}18` }}>
                        {modalIcon}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: modalColor }}>
                          Pianificazione
                        </div>
                        <div className="text-sm font-black text-gray-900 dark:text-gray-100 leading-tight">
                          {modalLabel}
                        </div>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={() => { setActiveModalConfig(null); setTempConfig(null); }}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all text-lg leading-none"
                    >
                      ×
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-4 no-scrollbar">
                  {/* Variazione immediata */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400 block">
                      Capitale iniziale oggi
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">€</span>
                      <input
                        type="number"
                        value={tempConfig.immediateChange || ''}
                        onChange={(e) => setTempConfig({ ...tempConfig, immediateChange: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                        placeholder="+500 aggiunta, −200 prelievo"
                        className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 text-sm font-bold text-gray-900 dark:text-gray-100 rounded-xl pl-8 pr-3 py-2.5 outline-none transition-all"
                        style={{ '--tw-ring-color': `${modalColor}60` }}
                      />
                    </div>
                    <p className="text-[10px] text-gray-400">Aggiustamento una-tantum al saldo attuale</p>
                  </div>

                  {/* Fasi di contribuzione */}
                  <div className="space-y-2.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400 block">
                      Rate mensili
                    </label>
                    
                    <div className="space-y-2">
                      {tempConfig.phases.map((phase, idx) => (
                        <div key={idx} className="rounded-2xl p-3 space-y-2.5"
                          style={{ background: `${modalColor}08`, border: `1px solid ${modalColor}20` }}>
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] uppercase tracking-wider font-extrabold" style={{ color: modalColor }}>
                              {idx === 0 ? 'Fase principale' : `Fase ${idx + 1}`}{idx === tempConfig.phases.length - 1 && (phase.endYear === null || phase.endYear === undefined || phase.endYear === 2100) ? ' · illimitata' : phase.endYear ? ` · fino al ${phase.endYear}` : ''}
                            </span>
                            {tempConfig.phases.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = tempConfig.phases.filter((_, pIdx) => pIdx !== idx);
                                  setTempConfig({ ...tempConfig, phases: updated });
                                }}
                                className="text-[9px] font-black text-red-400 hover:text-red-600 uppercase tracking-wider"
                              >
                                Rimuovi
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <span className="text-[9px] font-bold text-gray-400 uppercase block">Rata/mese (€)</span>
                              <input
                                type="number"
                                value={phase.amount || ''}
                                onChange={(e) => {
                                  const updated = [...tempConfig.phases];
                                  updated[idx].amount = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                  setTempConfig({ ...tempConfig, phases: updated });
                                }}
                                placeholder="es. 200"
                                className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-900 dark:text-gray-100 rounded-xl px-3 py-2 outline-none focus:ring-2 transition-all"
                              />
                            </div>

                            <div className="space-y-1">
                              <span className="text-[9px] font-bold text-gray-400 uppercase block">Fino al (anno)</span>
                              <input
                                type="number"
                                value={phase.endYear || ''}
                                min={currentYear + 1}
                                max={currentYear + 60}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const updated = [...tempConfig.phases];
                                  const ey = val === '' ? null : parseInt(val, 10);
                                  updated[idx].endYear = ey;
                                  // Auto-add next open phase if last phase now has an endYear and amount
                                  if (idx === updated.length - 1 && ey && ey > currentYear && Number(phase.amount) !== 0) {
                                    updated.push({ amount: 0, endYear: 2100 });
                                  }
                                  setTempConfig({ ...tempConfig, phases: updated });
                                }}
                                placeholder="∞ illimitata"
                                className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-900 dark:text-gray-100 rounded-xl px-3 py-2 outline-none focus:ring-2 transition-all"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...tempConfig.phases, { amount: 0, endYear: 2100 }];
                        setTempConfig({ ...tempConfig, phases: updated });
                      }}
                      className="w-full py-2 border border-dashed text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xs font-bold rounded-xl transition-all"
                      style={{ borderColor: `${modalColor}40` }}
                    >
                      + Aggiungi fase
                    </button>
                  </div>
                </div>

                <div className="px-5 pb-5 pt-3 border-t border-gray-100 dark:border-gray-800 flex gap-2.5">
                  <button
                    type="button"
                    onClick={() => { setActiveModalConfig(null); setTempConfig(null); }}
                    className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-sm font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={saveConfig}
                    className="flex-1 py-2.5 text-white text-sm font-bold rounded-xl shadow-lg transition-all hover:brightness-110 active:scale-[0.98]"
                    style={{ background: `linear-gradient(135deg, ${modalColor}, ${modalColor}cc)`, boxShadow: `0 4px 12px ${modalColor}30` }}
                  >
                    Salva
                  </button>
                </div>
              </div>
            </div>
            );
          })()}
          </div>
        )
      )}

      {/* ═══════════════════════════════════ TITOLI ═══════════════════════ */}
      {activeSection === 'securities' && (
        <div className="space-y-3 animate-fade-in-up">
          {securityWallets.length === 0 ? (
            <div className="card p-8 text-center space-y-2">
              <TrendingUp className="w-10 h-10 text-gray-300 mx-auto" />
              <p className="text-sm font-semibold text-gray-500">Nessun conto titoli con saldo positivo</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-1">
                <span className="text-xs font-bold text-gray-500">{securityWallets.length} conti · {fmt(securityValue)} totale</span>
                <span className="text-[10px] text-gray-400">Tap su un conto per espandere</span>
              </div>
              {securityWallets.map(w => (
                <SecurityCard key={w.id} wallet={w} portfolioValue={securityValue} walletFlows={walletFlows} allExpenses={expenses} allIncomes={incomes} />
              ))}
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════ DEPOSITI ═════════════════════ */}
      {activeSection === 'deposits' && (
        <div className="space-y-4 animate-fade-in-up">
          {depositWallets.length === 0 ? (
            <div className="card p-8 text-center space-y-2">
              <Shield className="w-10 h-10 text-gray-300 mx-auto" />
              <p className="text-sm font-semibold text-gray-500">Nessun conto deposito con saldo positivo</p>
              <p className="text-xs text-gray-400">Crea un conto di tipo "Conto Deposito" e inserisci il tasso lordo annuo.</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="card p-4">
                <SectionTitle icon={Calculator} title="Riepilogo Interessi Annui" sub="Stima su saldo attuale e tasso configurato" color="#10b981" />
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { l: 'Capitale totale', v: fmt(depositValue), c: 'bg-gray-50 dark:bg-gray-800/50' },
                    { l: 'Lordi/anno',      v: fmt(totalGross),   c: 'bg-yellow-50 dark:bg-yellow-900/10 text-yellow-700 dark:text-yellow-400' },
                    { l: 'Netti/anno',      v: fmt(totalNet),     c: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400' },
                  ].map(({ l, v, c }) => (
                    <div key={l} className={`rounded-xl p-2.5 ${c}`}>
                      <div className="text-[10px] font-semibold text-gray-500 mb-0.5">{l}</div>
                      <div className="text-sm font-black">{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Per-deposit cards */}
              {depositWallets.map(w => (
                <DepositCard key={w.id} wallet={w} totalDepositBalance={depositValue} />
              ))}

              {/* Comparison bar if multiple */}
              {depositWallets.length > 1 && depositProj.some(d => d.rate > 0) && (
                <div className="card p-4">
                  <SectionTitle icon={BarChart2} title="Confronto Rendimenti" sub="Interessi netti/anno per conto" color="#10b981" />
                  <div style={{ height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={depositProj.filter(d => d.rate > 0).map(d => ({ name: d.wallet.name, Lordi: d.gross, Netti: d.net }))} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
                        <Tooltip content={<InvTooltip />} />
                        <Bar dataKey="Lordi" fill="#d1fae5" radius={[4, 4, 0, 0]} maxBarSize={28} />
                        <Bar dataKey="Netti" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════ ETF ══════════════════════════ */}
      {activeSection === 'etf' && (
        <div className="animate-fade-in-up">
          <SecuritySection
            wallets={etfWallets}
            allWallets={allEtfWallets}
            incomes={incomes}
            expenses={expenses}
            portfolioValue={etfValue}
            totalPatrimony={totalPatrimony}
            updateDocument={updateDocument}
            sectionColor={TYPE_COLOR.etf}
          />
        </div>
      )}

      {/* ═══════════════════════════════════ AZIONI ═══════════════════════ */}
      {activeSection === 'azioni' && (
        <div className="animate-fade-in-up">
          <SecuritySection
            wallets={azioniWallets}
            allWallets={allAzioniWallets}
            incomes={incomes}
            expenses={expenses}
            portfolioValue={azioniValue}
            totalPatrimony={totalPatrimony}
            updateDocument={updateDocument}
            sectionColor={TYPE_COLOR.azioni}
          />
        </div>
      )}

      {/* ═══════════════════════════════════ OBBLIGAZIONI ═════════════════ */}
      {activeSection === 'obbligazioni' && (
        <div className="animate-fade-in-up">
          <SecuritySection
            wallets={obbligazioniWallets}
            allWallets={allObbligazioniWallets}
            incomes={incomes}
            expenses={expenses}
            portfolioValue={obbligazioniValue}
            totalPatrimony={totalPatrimony}
            updateDocument={updateDocument}
            sectionColor={TYPE_COLOR.obbligazioni}
          />
        </div>
      )}

      {/* ═══════════════════════════════════ DEPOSITO ════════════════════ */}
      {activeSection === 'deposito' && (
        <div className="animate-fade-in-up">
          <DepositSection
            wallets={depositWallets}
            allWallets={allDepositWallets}
            incomes={incomes}
            expenses={expenses}
            portfolioValue={depositValue}
            totalPatrimony={totalPatrimony}
            updateDocument={updateDocument}
          />
        </div>
      )}

      {/* ═══════════════════════════════════ CONSULENTE AI ═══════════════ */}
      {activeSection === 'ai' && (
        <div className="animate-fade-in-up">
          <InvestmentAIAdvisor
            userSettings={userSettings}
            onUpdateSettings={onUpdateSettings}
            wallets={wallets}
            expenses={expenses}
            incomes={incomes}
            debts={debts}
            addDocument={addDocument}
            pacAttuale={pacAttuale}
            liquidValue={liquidValue}
            depositValue={depositValue}
            etfValue={etfValue}
            azioniValue={azioniValue}
            obbligazioniValue={obbligazioniValue}
            avgExpectedEtf={avgExpectedEtf}
            avgExpectedAzioni={avgExpectedAzioni}
            avgExpectedObbl={avgExpectedObbl}
            avgExpectedDeposit={avgExpectedDeposit}
          />
        </div>
      )}

      {/* ═══════════════════════════════════ ALLOCAZIONE (rimosso) ════════ */}
      {false && activeSection === 'allocation' && (
        <div className="space-y-4 animate-fade-in-up">
          {/* Securities donut */}
          {securityWallets.length > 0 && (
            <div className="card p-4">
              <SectionTitle icon={PieChartIcon} title="Allocazione Titoli" sub="Solo ETF · Azioni · Obbligazioni (senza depositi)" color="#6366f1" />
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={allocationByType} cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={4} dataKey="value" nameKey="name" strokeWidth={0}>
                      {allocationByType.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip content={<InvTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5 mt-2">
                {allocationByType.map(a => (
                  <div key={a.type} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 font-semibold text-gray-700 dark:text-gray-300">
                        <span>{TYPE_ICON[a.type]}</span> {a.name}
                      </span>
                      <span className="font-black text-gray-900 dark:text-gray-100">
                        {fmt(a.value)} <span className="text-gray-400 font-normal">({a.pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${a.pct}%`, background: a.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Individual wallets ranking */}
          <div className="card p-4">
            <SectionTitle icon={Layers} title="Peso per Conto" sub="Tutti i conti investimento attivi" color="#f59e0b" />
            <div className="space-y-2.5">
              {[...securityWallets, ...depositWallets]
                .sort((a, b) => (b.balance || 0) - (a.balance || 0))
                .map(w => {
                  const t = classifyWallet(w);
                  const color = w.color || TYPE_COLOR[t] || '#888';
                  const bal = w.balance || 0;
                  return (
                    <div key={w.id} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="flex-shrink-0">{TYPE_ICON[t]}</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300 truncate">{w.name}</span>
                          <span className="text-[9px] px-1 py-0.5 rounded font-bold flex-shrink-0" style={{ background: `${color}18`, color }}>
                            {TYPE_LABEL[t]}
                          </span>
                        </div>
                        <span className="font-black text-gray-900 dark:text-gray-100 ml-2 flex-shrink-0">
                          {pct(bal, totalPatrimony)}%
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct(bal, totalPatrimony)}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Diversification score (securities only) */}
          {securityWallets.length > 0 && (
            <div className="card p-4">
              <SectionTitle icon={Info} title="Score Diversificazione" sub="Analisi concentrazione portafoglio titoli" color="#f59e0b" />
              {(() => {
                const top = allocationByType[0];
                const concentration = top ? parseFloat(top.pct) : 0;
                const numTypes = allocationByType.length;
                let score, label, color;
                if (concentration > 80 || numTypes === 1) { score = 28; label = 'Concentrato'; color = '#ef4444'; }
                else if (concentration > 60 || numTypes === 2) { score = 55; label = 'Parzialmente diversificato'; color = '#f59e0b'; }
                else { score = 85; label = 'Ben diversificato'; color = '#10b981'; }
                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Punteggio</span>
                      <span className="text-2xl font-black" style={{ color }}>{score}/100</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${score}%`, background: color }} />
                    </div>
                    <div className="text-xs font-bold" style={{ color }}>{label}</div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-2.5 text-center">
                        <div className="text-[10px] text-gray-500 font-semibold">Tipologie</div>
                        <div className="text-lg font-black text-gray-900 dark:text-gray-100">{numTypes}</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-2.5 text-center">
                        <div className="text-[10px] text-gray-500 font-semibold">Concentrazione max</div>
                        <div className="text-lg font-black text-gray-900 dark:text-gray-100">{concentration}%</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
